import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Chip, Typography, Stack, Tooltip, Popover, Dialog, DialogContent, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import Chart from 'react-apexcharts';
import { useL } from './LangContext';
import { getPoNetOfVat, getCommittedCostValue, getEstimatedProfit, getProjectedFund } from './formulas';
import { DevCardBadge } from '../../DevPageBadge';

// เดียวกับ sequential blue ที่ dataviz skill กำหนด — ใช้สีเดียวเพราะทุกกราฟที่นี่เป็น
// single-series (ขนาด PO) ไม่ใช่การเทียบ identity ข้ามกลุ่ม
const BLUE = '#2a78d6';
const GRID = '#e1e0d9';
const AXIS_INK = '#898781';
const TEXT_SECONDARY = '#52514e';
const FADED_TEXT = 'rgba(82, 81, 78, 0.5)'; // TEXT_SECONDARY จางลง 50%
const INCOMPLETE_GREEN = '#16a34a'; // มีโครงการที่ยังดำเนินการไม่เสร็จ 100%
const LAWSUIT_MAROON = '#991b1b'; // สีเลือดหมู — ใช้ hex เดียวกับ legal_lawsuit chip ที่อื่นในระบบ (constants.js)
const YOY_RED = '#dc2626'; // เส้นอ้างอิง YoY บนกราฟ "แนวโน้ม PO รายปี"
// สีไตรมาส Q1-Q4 — 4 slot แรกของ categorical palette ที่ dataviz skill validate ไว้แล้วว่าผ่าน
// CVD/contrast ทั้ง adjacent-pair และ all-pairs (ใช้ได้ปลอดภัยแม้ผู้ใช้ตาบอดสี)
const QUARTER_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
// G-11: เส้นเทียบปี — ใช้ palette เดียวกับ QUARTER_COLORS วนตามลำดับปี (เก่า→ใหม่)
const MONTH_LABELS_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const MONTH_LABELS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PROFIT_GREEN = '#0ca30c'; // การ์ด "กำไรสูงสุด" — status "good" token จาก dataviz skill
const ID_RED = '#f87171'; // G-4: แท่งลำดับที่ 3 (ID PAID) ใช้สีแดงอ่อน
const G4_PROFIT_GREEN = '#166534'; // G-4: แท่งลำดับที่ 2 (Profit) ใช้เขียวเข้ม
const NET_PROFIT_POSITIVE = '#22c55e'; // G-4: Net Profit >= 0 เขียวสด
const NET_PROFIT_NEGATIVE = '#ef4444'; // G-4: Net Profit < 0 แดงสด
const OVER_BUDGET_ORANGE = '#eb6834'; // การ์ด "เกิน budget มากที่สุด"
const LOSS_RED = '#dc2626'; // การ์ด "ขาดทุนสูงสุด" — สีเดียวกับ YOY_RED (แดง = ลบ/แย่ ทั้งคู่)
// G-10: บริษัทที่โปรเจกต์ล่าสุด "ไม่ใช่ปีนี้" ใช้สีทึบ/เข้มกว่า (แยกจากปีนี้ที่ใช้สีสดของด้านบน)
// ยังคงเขียว=กำไร/แดง=ขาดทุนเหมือนเดิม แค่ทึบลง ไม่ใช้เทาแบบ G-7/8/9 เพราะ G-10 ต้องคงความหมาย
// กำไร/ขาดทุนไว้ (เทาจะทำให้แยกไม่ออกว่าแท่งไหนกำไร/ขาดทุน)
const PROFIT_GREEN_MUTED = '#166534';
const LOSS_RED_MUTED = '#7f1d1d';
const TOP_LIST_CARD_HEIGHT = 336; // ลดลง 30% จาก 480 ตามที่ขอ (ไม่แตะกรอบ scroll คอนเทนเนอร์นอกสุด)
// G-5/G-6 (RankedCustomerList) เท่านั้น — สูงกว่า TOP_LIST_CARD_HEIGHT ปกติ 50px ตามที่ขอ แยกค่า
// ออกมาต่างหาก ไม่แก้ TOP_LIST_CARD_HEIGHT ตรงๆ เพราะตัวนั้นยังใช้ร่วมกับ G-7/8/9 อยู่ ไม่อยากกระทบ
const RANKED_LIST_CARD_HEIGHT = TOP_LIST_CARD_HEIGHT + 50;
const TOP_LIST_COUNT = 10;
const COMPANY_CHART_COUNT = 30; // G-10 — ลองแสดง 30 บริษัทล่าสุดแทน 10 (TOP_LIST_COUNT ของ G-7/8/9 ไม่เกี่ยว)

// คู่ค้ายอด PO เกินเกณฑ์นี้ต้องขึ้นแยกเป็นรายชื่อของตัวเอง ห้ามถูกยุบรวมเข้า "อื่นๆ"
// ไม่ว่าจะอยู่นอกอันดับต้นๆ แค่ไหนก็ตาม — มีแต่รายที่ <= เกณฑ์นี้เท่านั้นที่ถูกยุบรวม
const OTHERS_THRESHOLD = 100000;
// กราฟ "แนวโน้ม PO รายปี" กรองเฉพาะปีนี้เป็นต้นไป (ตัดปีที่ข้อมูลวันที่ผิดปกติ/เก่าเกินไปทิ้ง)
const YEAR_TREND_MIN = 2025;
// เกณฑ์มูลค่าขั้นต่ำของ 2 การ์ด "คู่ค้าปีนี้"/"คู่ค้าเก่าที่เงียบปีนี้" — บังเอิญเท่ากับ
// OTHERS_THRESHOLD ด้านบน แต่แยกค่าคนละตัวตั้งใจ เพราะคนละความหมาย (ที่นั่นคือเกณฑ์จัดกลุ่มกราฟ
// ส่วนนี้คือเกณฑ์ "มูลค่ามากกว่า 1 แสนบาท" ตามที่ผู้ใช้ขอ) กันพลาดถ้าใครแก้ค่าใดค่าหนึ่งทีหลัง
const CUSTOMER_RETENTION_THRESHOLD = 100000;
// กรอบการ์ดทั้ง 2 ใบสูงเท่ากันเป๊ะเสมอ (อิงตามการ์ดขวา "แนวโน้ม PO รายปี" ที่มีแค่หัวเรื่องบรรทัด
// เดียว เลยเตี้ยสุด) — การ์ดซ้ายมี header เพิ่ม (ปุ่มเรียง/legend/ป้าย "Latest Project cost")
// สูงไม่เท่ากันในแต่ละโหมด เลยใช้ flex:1 ให้พื้นที่กราฟหด/ขยายชดเชยเอาเอง แทนที่จะ fix เผื่อ
// พลาดพลั้งคำนวณ px เอง — ดู CARD_HEIGHT ใช้คู่กับ display:flex บน Card ทั้ง 2 ใบ
const CARD_HEIGHT = 460;
// G-3/G-4/G-11 (แถวเดียวกัน) — ลดกรอบ Card ทั้งใบลง 20% จาก CARD_HEIGHT ก่อน แล้วสั้นไป เลยเพิ่ม
// กลับมาอีก 10% จากค่าที่ลดไปแล้ว (ไม่ใช่จาก CARD_HEIGHT ตรงๆ)
const TOP_TRIO_CARD_HEIGHT = Math.round(CARD_HEIGHT * 0.8 * 1.1);
// G-10 เท่านั้น — สูงกว่า CARD_HEIGHT ปกติ 20% ตามที่ขอ แยกค่าออกมาต่างหาก ไม่แก้ CARD_HEIGHT ตรงๆ
// เพราะ TOP_TRIO_CARD_HEIGHT ยังคำนวณอิงจากมันอยู่ (จะกระทบ G-3/G-4/G-11 ไปด้วยถ้าแก้ตรงนั้น)
const G10_CARD_HEIGHT = Math.round(CARD_HEIGHT * 1.2);

const fmtBaht = (n) =>
  Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 });

function customerName(row, L) {
  return row?.client_id?.name || row?.customer || L('ไม่ระบุคู่ค้า', 'Unspecified');
}

function jobDate(row) {
  const d = row?.oldest_job_date || row?.createdAt;
  return d ? new Date(d) : null;
}

function jobQuarter(d) {
  return Math.floor(d.getMonth() / 3); // 0-3 = Q1-Q4
}

// (08/26) — เดือน/ปี ค.ศ. 2 หลักท้าย ของวันที่ล่าสุด
function fmtMonYear(d) {
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear() % 100).padStart(2, '0');
  return `${mm}/${yy}`;
}

// ตารางรายเดือน (G-4 แท็บรายเดือน) — เลือกปีได้ เรียงเดือนล่าสุดไว้บน มีแถวรวมท้ายตาราง
// คลิกแถวเดือนเพื่อเปิด popup รายการโครงการของเดือนนั้น (byMonth = [['YYYY-MM', {po,profit,id,count,jobs,idJobs}], ...])
const MONTH_TABLE_CELL_SX = { py: 0.5, px: 1, fontSize: '0.75rem', whiteSpace: 'nowrap' };
function MonthTable({ byMonth, L }) {
  const years = [...new Set(byMonth.map(([key]) => key.slice(0, 4)))];
  const [year, setYear] = useState(years[years.length - 1]);
  const [openKey, setOpenKey] = useState(null);
  const rows = byMonth.filter(([key]) => key.startsWith(year)).reverse();
  const sum = (pick) => rows.reduce((acc, [, v]) => acc + pick(v), 0);
  const netColor = (n) => (n < 0 ? '#b91c1c' : 'inherit');
  const numCell = (n, color) => (
    <TableCell align="right" sx={{ ...MONTH_TABLE_CELL_SX, color }}>
      {fmtBaht(n)}
    </TableCell>
  );
  const headSx = { ...MONTH_TABLE_CELL_SX, fontWeight: 700, bgcolor: '#f6f5ef' };
  const totalNet = sum((v) => v.profit - v.id);
  const detailEntry = openKey ? byMonth.find(([key]) => key === openKey) : null;
  const detail = detailEntry
    ? { v: detailEntry[1], label: `${detailEntry[0].slice(5)}/${detailEntry[0].slice(2, 4)}` }
    : null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Stack direction="row" spacing={0.5} sx={{ mb: 0.75, flexWrap: 'wrap', flexShrink: 0 }}>
        {years.map((y) => (
          <Chip
            key={y}
            label={y}
            size="small"
            onClick={() => {
              setYear(y);
              setOpenKey(null);
            }}
            color={year === y ? 'primary' : 'default'}
            variant={year === y ? 'filled' : 'outlined'}
            sx={{ height: 20, fontSize: '0.68rem' }}
          />
        ))}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>{L('เดือน', 'Month')}</TableCell>
              <TableCell align="right" sx={headSx}>{L('โครงการ', 'Projects')}</TableCell>
              <TableCell align="right" sx={headSx}>PO</TableCell>
              <TableCell align="right" sx={headSx}>{L('กำไร', 'Profit')}</TableCell>
              <TableCell align="right" sx={headSx}>ID PAID</TableCell>
              <TableCell align="right" sx={headSx}>{L('กำไรสุทธิ', 'Net Profit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([key, v]) => {
              const [y, m] = key.split('-');
              const net = v.profit - v.id;
              return (
                <React.Fragment key={key}>
                  <TableRow
                    hover
                    onClick={() => setOpenKey(key)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={MONTH_TABLE_CELL_SX}>{`${m}/${y.slice(2)}`}</TableCell>
                    <TableCell align="right" sx={MONTH_TABLE_CELL_SX}>{v.count}</TableCell>
                    {numCell(v.po)}
                    {numCell(v.profit, netColor(v.profit))}
                    {numCell(v.id)}
                    {numCell(net, netColor(net))}
                  </TableRow>
                </React.Fragment>
              );
            })}
            <TableRow>
              <TableCell sx={{ ...MONTH_TABLE_CELL_SX, fontWeight: 700 }}>{L('รวม', 'Total')}</TableCell>
              <TableCell align="right" sx={{ ...MONTH_TABLE_CELL_SX, fontWeight: 700 }}>{sum((v) => v.count)}</TableCell>
              {[sum((v) => v.po), sum((v) => v.profit), sum((v) => v.id), totalNet].map((n, i) => (
                <TableCell
                  key={i}
                  align="right"
                  sx={{ ...MONTH_TABLE_CELL_SX, fontWeight: 700, color: i === 3 ? netColor(n) : undefined }}
                >
                  {fmtBaht(n)}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      {/* popup รายละเอียดโครงการของเดือนที่คลิก */}
      <Dialog open={!!detail} onClose={() => setOpenKey(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                  {L(`รายละเอียดเดือน ${detail.label}`, `Details for ${detail.label}`)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>
                  {`PO ${fmtBaht(detail.v.po)} · ${L('กำไร', 'Profit')} ${fmtBaht(detail.v.profit)} · ID PAID ${fmtBaht(detail.v.id)} · ${L('กำไรสุทธิ', 'Net Profit')} ${fmtBaht(detail.v.profit - detail.v.id)}`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setOpenKey(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <DialogContent dividers sx={{ pt: 1 }}>
              {detail.v.jobs.length === 0 && detail.v.idJobs.length === 0 && (
                <Typography sx={{ fontSize: '0.8rem', color: TEXT_SECONDARY, py: 3, textAlign: 'center' }}>
                  {L('ไม่มีรายการ', 'No items')}
                </Typography>
              )}
              {detail.v.jobs.length > 0 && (
                <>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 0.5 }}>
                    {L('โครงการ (PO / กำไร)', 'Projects (PO / Profit)')}
                  </Typography>
                  <Table size="small" sx={{ mb: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headSx}>{L('เลขงาน', 'Job No.')}</TableCell>
                        <TableCell sx={headSx}>{L('โครงการ', 'Project')}</TableCell>
                        <TableCell sx={headSx}>{L('ผู้ว่าจ้าง', 'Customer')}</TableCell>
                        <TableCell align="right" sx={headSx}>PO</TableCell>
                        <TableCell align="right" sx={headSx}>{L('กำไร', 'Profit')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.v.jobs.map((job) => (
                        <TableRow key={`po-${job.id}`}>
                          <TableCell sx={MONTH_TABLE_CELL_SX}>{job.code}</TableCell>
                          <TableCell sx={{ ...MONTH_TABLE_CELL_SX, whiteSpace: 'normal' }}>{job.name}</TableCell>
                          <TableCell sx={{ ...MONTH_TABLE_CELL_SX, whiteSpace: 'normal' }}>{job.customerName}</TableCell>
                          {numCell(job.value)}
                          {numCell(job.profit, netColor(job.profit))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
              {detail.v.idJobs.length > 0 && (
                <>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 0.5 }}>ID PAID</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headSx}>{L('เลขงาน', 'Job No.')}</TableCell>
                        <TableCell sx={headSx}>{L('โครงการ', 'Project')}</TableCell>
                        <TableCell sx={headSx}>{L('ผู้ว่าจ้าง', 'Customer')}</TableCell>
                        <TableCell align="right" sx={headSx}>ID PAID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.v.idJobs.map((job) => (
                        <TableRow key={`id-${job.id}`}>
                          <TableCell sx={MONTH_TABLE_CELL_SX}>{job.code}</TableCell>
                          <TableCell sx={{ ...MONTH_TABLE_CELL_SX, whiteSpace: 'normal' }}>{job.name}</TableCell>
                          <TableCell sx={{ ...MONTH_TABLE_CELL_SX, whiteSpace: 'normal' }}>{job.customerName}</TableCell>
                          {numCell(job.value)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

// กราฟรายเดือน (G-4): โชว์ ~5 เดือนต่อหน้าจอ ที่เหลือเลื่อนแนวนอนได้ — ค่าเริ่มต้นชิดเดือนล่าสุด
// (ขวาสุด) แล้วเลื่อนไปซ้าย จำตำแหน่งที่เลื่อนไว้ใน posRef (+ localStorage กันรีเฟรช/สลับแท็บ)
const MONTHS_VISIBLE = 5;
const MONTH_TIP_WIDTH = 230;
const MONTH_SCROLL_KEY = 'po_overview_g4_month_scroll';
function MonthScrollChart({ options, series, count, posRef, clickTimerRef }) {
  const wrapRef = useRef(null);
  const widthPct = Math.max(100, (count / MONTHS_VISIBLE) * 100);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let saved = posRef.current;
    if (saved == null) {
      try {
        const raw = localStorage.getItem(MONTH_SCROLL_KEY);
        if (raw != null) saved = Number(raw);
      } catch (e) {
        // ไม่มี localStorage ก็ใช้ค่าเริ่มต้น
      }
    }
    // รอ apex วาดเสร็จให้ scrollWidth เต็มก่อนค่อยเลื่อน
    const t = setTimeout(() => {
      el.scrollLeft = saved != null && !Number.isNaN(saved) ? saved : el.scrollWidth;
    }, 50);
    return () => clearTimeout(t);
  }, [posRef, count]);

  const handleScroll = (e) => {
    const left = e.currentTarget.scrollLeft;
    posRef.current = left;
    try {
      localStorage.setItem(MONTH_SCROLL_KEY, String(left));
    } catch (err) {
      // ignore
    }
  };

  // legend + แกน Y ตรึงอยู่กับที่ ไม่เลื่อนตาม: legend เป็น JSX เอง, แกน Y วาดจากกราฟตัวที่ 2
  // (ข้อมูล/ความสูง/xaxis เหมือนกันเป๊ะเพื่อให้สเกลและแนวตั้งตรงกัน แต่แท่ง/เส้น/ตัวอักษรแกน X โปร่งใส)
  // ส่วนกราฟหลักซ่อนตัวเลขแกน Y เหลือแค่แท่งกับแกน X ที่เลื่อนได้
  // tooltip ของ apex ปิดไว้ (มันบังแท่งของเดือนตัวเอง + โดน overflow ของกล่อง scroll ตัด) — ใช้กล่อง
  // position:fixed เองแทน วางข้างกลุ่มแท่งของเดือนที่ชี้ ฝั่งขวาถ้าพื้นที่พอ ไม่พอก็ฝั่งซ้าย
  const [tip, setTip] = useState(null);
  // หาเดือนจากตำแหน่งเมาส์เทียบกรอบ plot (.apexcharts-grid) แทนการพึ่ง event ของ apex — เดือนที่ค่า 0
  // (ไม่มีแท่งให้ชี้) ก็ยังโชว์ได้ และแต่ละเดือนกว้างเท่ากัน = กรอบ plot / จำนวนเดือน
  const showTip = (event) => {
    const grid = wrapRef.current?.querySelector('.apexcharts-grid');
    if (!grid) return;
    const rect = grid.getBoundingClientRect();
    const slot = rect.width / count;
    const idx = Math.floor((event.clientX - rect.left) / slot);
    if (idx < 0 || idx >= count || event.clientY < rect.top || event.clientY > rect.bottom) {
      setTip(null);
      return;
    }
    const left = rect.left + idx * slot;
    setTip({ idx, left, right: left + slot, y: event.clientY });
  };
  // ดับเบิลคลิกเดือนไหน = ซูมแกน Y ให้เป็นช่วงของเดือนนั้น (ดับเบิลคลิกเดือนเดิมซ้ำ = กลับสเกลปกติ)
  // ช่วงปัดเป็นทีละ 2,000,000 (สูงสุด ≥ 2M ขั้นต่ำ, ติดลบก็ขยายลงใต้ 0 ในหน่วยเดียวกัน) ขีดละ 200,000
  // — เดือนค่าน้อยมากก็ยังเห็น/คลิกแท่งได้เพราะสเกลไม่ถูกดึงไปตามเดือนอื่น
  const [zoomIdx, setZoomIdx] = useState(null);
  let yaxisOptions = options.yaxis;
  let zoomExtreme = null;
  if (zoomIdx != null) {
    const vals = series.map((s) => s.data[zoomIdx] || 0);
    // โฟกัสเฉพาะเดือนที่เลือก: ปัดขึ้นทีละ 200,000 (สูงสุด 90,000 → 0–200,000) ติดลบก็ปัดลงทีละ
    // 200,000; ช่วงเล็กกว่า 2M แบ่ง 10 ขีดเท่าๆ กัน, ตั้งแต่ 2M ขึ้นไปขีดละ 200,000 (เดือนใหญ่ยังมี 10+ จุด)
    const block = 200000;
    const yMax = Math.max(block, Math.ceil(Math.max(0, ...vals) / block) * block);
    const yMin = Math.min(0, ...vals) < 0 ? Math.floor(Math.min(0, ...vals) / block) * block : 0;
    const range = yMax - yMin;
    yaxisOptions = {
      ...options.yaxis,
      min: yMin,
      max: yMax,
      tickAmount: range >= 1000000 ? range / 200000 : 10,
    };
    zoomExtreme = Math.max(Math.abs(yMin), Math.abs(yMax));
  }
  const handleDoubleClick = (event) => {
    clearTimeout(clickTimerRef.current); // ไม่ให้ popover ของคลิกแรกเด้งตามมา
    const grid = wrapRef.current?.querySelector('.apexcharts-grid');
    if (!grid) return;
    const rect = grid.getBoundingClientRect();
    const idx = Math.floor((event.clientX - rect.left) / (rect.width / count));
    if (idx < 0 || idx >= count) return;
    setZoomIdx((cur) => (cur === idx ? null : idx));
  };
  const mainOptions = {
    ...options,
    tooltip: { enabled: false },
    legend: { show: false },
    grid: { ...options.grid, padding: { left: 0, right: 8 } },
    yaxis: { ...yaxisOptions, labels: { ...options.yaxis.labels, show: false } },
  };
  const axisOptions = {
    ...options,
    yaxis: yaxisOptions,
    legend: { show: false },
    colors: series.map(() => 'transparent'),
    chart: { ...options.chart, events: {}, animations: { enabled: false } },
    grid: { show: false, padding: { left: 0, right: 0 } },
    tooltip: { enabled: false },
    xaxis: {
      ...options.xaxis,
      labels: { ...options.xaxis.labels, style: { ...options.xaxis.labels.style, colors: 'transparent' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
  };

  // ความกว้างช่องแกน Y ปรับตามตัวเลขที่ยาวที่สุด (ประมาณ 6.5px/ตัวอักษร ที่ฟอนต์ 11px + ขอบ)
  // เผื่อ tick สูงสุดที่ปัดขึ้นด้วยค่า 1 ตัวเลขข้างหน้า (เช่น 9.5M → 10M)
  const allValues = series.flatMap((s) => s.data);
  const extreme =
    zoomExtreme ?? Math.max(Math.abs(Math.min(0, ...allValues)), Math.max(0, ...allValues)) * 1.25;
  const axisLabelLen = Math.max(
    String(options.yaxis.labels.formatter(extreme)).length,
    String(options.yaxis.labels.formatter(-extreme)).length,
  );
  const axisWidth = Math.max(48, Math.ceil(axisLabelLen * 6.5) + 20);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ flexShrink: 0, flexWrap: 'wrap', mb: 0.5 }}>
        {series.map((s, i) => (
          <Stack key={s.name} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: options.colors[i] }} />
            <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>{s.name}</Typography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <Box sx={{ width: axisWidth, flexShrink: 0, overflow: 'hidden' }}>
          <Chart options={axisOptions} series={series} type="bar" height="100%" />
        </Box>
        <Box
          ref={wrapRef}
          onDoubleClick={handleDoubleClick}
          onMouseMove={showTip}
          onMouseLeave={() => setTip(null)}
          onScroll={(e) => {
            setTip(null);
            handleScroll(e);
          }}
          sx={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden' }}>
          <Box sx={{ width: `${widthPct}%`, height: '100%' }}>
            <Chart options={mainOptions} series={series} type="bar" height="100%" />
          </Box>
        </Box>
      </Box>
      {tip && (
        <Box
          sx={{
            position: 'fixed',
            zIndex: 2000,
            pointerEvents: 'none',
            width: MONTH_TIP_WIDTH,
            bgcolor: '#fff',
            border: '1px solid #e1e0d9',
            borderRadius: 1,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            p: 1.25,
            top: Math.min(Math.max(tip.y - 70, 8), window.innerHeight - 190),
            // ขวาของกลุ่มแท่งถ้าที่พอ ไม่พอก็ซ้าย — ไม่ทับแท่งของเดือนตัวเอง
            ...(window.innerWidth - tip.right >= MONTH_TIP_WIDTH + 12
              ? { left: tip.right + 12 }
              : { right: window.innerWidth - tip.left + 12 }),
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', color: TEXT_SECONDARY, mb: 0.5 }}>
            {options.xaxis.categories[tip.idx]}
          </Typography>
          {series.map((s, i) => (
            <Stack key={s.name} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: options.colors[i] }} />
                <Typography sx={{ fontSize: '0.72rem', color: TEXT_SECONDARY }}>{s.name}</Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                {fmtBaht(s.data[tip.idx] || 0)} THB
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  );
}

const StatBox = ({ label, value, devNum, devName }) => (
  <Box
    sx={{
      position: 'relative',
      flex: 1,
      minWidth: 220,
      bgcolor: '#fcfcfb',
      border: '1px solid #e1e0d9',
      borderRadius: 2,
      px: 2.5,
      py: 2,
    }}
  >
    <DevCardBadge num={devNum} name={devName} />
    <Typography sx={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: '#0b0b0b', mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

// ลิสต์ breakdown รายโปรเจกต์ (โค้ด/ชื่อ + Mo/Yr + มูลค่า) — ใช้ร่วมกันทั้ง tooltip G-5/G-6 และ
// popover G-3 (double-click แท่ง) — แต่ละ instance มี state คลิกสลับ/เรียงเป็นของตัวเอง (1
// instance = 1 ทูลทิป/popover พอดี ไม่ต้องเก็บ Map แยกตามคู่ค้าเหมือนที่เคยทำใน RankedCustomerList)
// jobs: [{code,name,value,id,date,customerName,budget,po}] — showCustomerName ใช้กับแท่ง "อื่นๆ"
// ใน G-3 เท่านั้น (รวมหลายบริษัทปนกัน ต้องระบุว่าแต่ละแถวเป็นของใคร), showBudget ใช้กับ popover
// G-10 เท่านั้น (value ตรงนั้นคือกำไร/ขาดทุน ไม่ใช่ PO — โชว์คอลัมน์ PO + BG กำกับก่อนกำไร/ขาดทุน
// ให้เทียบกันได้ — คำนำหน้า "PO"/"BG" โชว์แค่แถวแรกพอ ไม่ต้องซ้ำทุกแถว)
const JobBreakdownList = ({
  jobs,
  total,
  onOpenProject,
  showCustomerName,
  showBudget,
  defaultSort = null,
}) => {
  const L = useL();
  const [showName, setShowName] = useState(false);
  const [sort, setSort] = useState(defaultSort); // { key: 'date'|'value', dir: 'asc'|'desc' } | null

  // toggle รหัส/ชื่อเปลี่ยนความสูงกล่องได้ (ชื่อยาวกว่ารหัสมักห่อหลายบรรทัด) ทำให้ Popper/Popover
  // ขยับตำแหน่งกลางอากาศระหว่างคลิกครั้งที่ 1–2 ของ double-click จนเมาส์หลุดเป้า — หน่วง toggle
  // ไว้ 300ms ก่อนค่อยสั่งจริง ถ้า double-click ทันภายในนั้นให้ยกเลิก toggle ไปเลย (นับเป็น
  // double-click ล้วนๆ ไม่ต้องสลับข้อความให้กวนสายตาระหว่างเปิดโปรเจกต์)
  const pendingToggleRef = useRef(null);
  const clearPendingToggle = () => {
    if (pendingToggleRef.current) {
      clearTimeout(pendingToggleRef.current);
      pendingToggleRef.current = null;
    }
  };
  const handleLabelClick = () => {
    clearPendingToggle();
    pendingToggleRef.current = setTimeout(() => {
      setShowName((prev) => !prev);
      pendingToggleRef.current = null;
    }, 300);
  };
  const handleJobDoubleClick = (jobId) => {
    clearPendingToggle();
    if (jobId && onOpenProject) onOpenProject(jobId);
  };
  useEffect(() => clearPendingToggle, []);

  // คลิกคอลัมน์ Mo/Yr หรือมูลค่าเพื่อเรียง job ตามคอลัมน์นั้น สลับเก่า/ใหม่หรือมาก/น้อยไปกลับได้
  // เก็บเป็น key เดียว { key, dir } ไม่ใช่ 2 state แยกกัน กันเคสเรียงตามวันที่ค้างอยู่แล้วมากด
  // เรียงมูลค่าซ้อน ต้องสลับ key ไปเลยไม่ใช่งงว่าเรียงตามอะไร — ไม่ทำให้กล่องเปลี่ยนความสูง
  // (แค่สลับลำดับแถว) เลยไม่ต้องหน่วงเหมือนตัว toggle รหัส/ชื่อด้านบน
  const toggleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev?.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const jobsToShow = sort
    ? [...(jobs || [])].sort((a, b) => {
        const va = sort.key === 'date' ? (a.date ? a.date.getTime() : 0) : a.value;
        const vb = sort.key === 'date' ? (b.date ? b.date.getTime() : 0) : b.value;
        return sort.dir === 'asc' ? va - vb : vb - va;
      })
    : jobs || [];

  return (
    <Box sx={{ py: 0.5, minWidth: 260, maxWidth: 420 }}>
      {jobsToShow.map((j, ji) => (
        <Stack
          key={ji}
          direction="row"
          spacing={1}
          onDoubleClick={() => handleJobDoubleClick(j.id)}
          sx={{ alignItems: 'center', cursor: j.id ? 'pointer' : 'default' }}
        >
          {/* minWidth ตั้งไว้ไม่ให้ 0 เหมือนก่อน — ตอนนี้แถวมีคอลัมน์ PO/BG เพิ่มมา ถ้าปล่อย 0
              รหัส/ชื่อโปรเจกต์จะโดนบีบจนตัวอักษรขึ้นบรรทัดใหม่ทีละตัว (เละ) เจอจริงมาแล้ว */}
          <Box sx={{ flex: 1, minWidth: 92 }}>
            {showCustomerName && (
              <Typography sx={{ fontSize: '0.62rem', opacity: 0.75, lineHeight: 1.3 }} noWrap>
                {j.customerName}
              </Typography>
            )}
            <Typography
              onClick={handleLabelClick}
              sx={{
                fontSize: '0.68rem',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: '2px',
                wordBreak: 'break-word',
              }}
              title={L(
                'คลิกเพื่อสลับรหัส/ชื่อโครงการ (สลับทั้งชุด) — ดับเบิลคลิกเพื่อเปิดโครงการ',
                'Click to toggle code/project name (toggles the whole list) — double-click to open the project',
              )}
            >
              {showName ? j.name : j.code}
            </Typography>
          </Box>
          {!showName && (
            <Typography
              onClick={() => toggleSort('date')}
              sx={{
                fontSize: '0.66rem',
                opacity: 0.85,
                flexShrink: 0,
                width: 34,
                textAlign: 'right',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: '2px',
              }}
              title={L('คลิกเพื่อเรียงตามวันที่ (สลับเก่า/ใหม่ไปกลับ)', 'Click to sort by date (toggle old/new)')}
            >
              {fmtMonYear(j.date)}
            </Typography>
          )}
          {showBudget && (
            <Typography
              sx={{
                fontSize: '0.66rem',
                opacity: 0.85,
                flexShrink: 0,
                width: 58,
                textAlign: 'right',
              }}
              title={L('ยอด PO', 'PO amount')}
            >
              {ji === 0 ? 'PO ' : ''}
              {fmtBaht(j.po)}
            </Typography>
          )}
          {showBudget && (
            <Typography
              sx={{
                fontSize: '0.66rem',
                opacity: 0.85,
                flexShrink: 0,
                width: 58,
                textAlign: 'right',
              }}
              title={L('งบประมาณ (BG) ที่ตั้งไว้', 'Budget (BG) allocated')}
            >
              {ji === 0 ? 'BG ' : ''}
              {fmtBaht(j.budget)}
            </Typography>
          )}
          <Typography
            onClick={() => toggleSort('value')}
            sx={{
              fontSize: '0.68rem',
              flexShrink: 0,
              width: 72,
              textAlign: 'right',
              cursor: 'pointer',
              textDecoration: 'underline dotted',
              textUnderlineOffset: '2px',
            }}
            title={L('คลิกเพื่อเรียงตามมูลค่า (สลับมาก/น้อยไปกลับ)', 'Click to sort by amount (toggle high/low)')}
          >
            {fmtBaht(j.value)}
          </Typography>
        </Stack>
      ))}
      {total != null && (
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.3)' }}
        >
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>{L('รวม', 'Total')}</Typography>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
            {fmtBaht(total)} {L('บาท', 'THB')}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

// ลิสต์อันดับคู่ค้า (ไม่ใช่กราฟแท่ง) — ใช้ร่วมกันทั้ง 2 การ์ด "คู่ค้าปีนี้"/"คู่ค้าเก่าที่เงียบปีนี้"
// entries: [[ชื่อคู่ค้า, มูลค่า, "MM/YY" งานล่าสุด, จำนวน job, [{code,name,value}]], ...] เรียงมากไปน้อยแล้ว
// autoHeight: การ์ดสูงตามเนื้อหาจริง (ไม่ fix + ไม่ scroll ใน) — ใช้กับ G-12 ที่จำนวนรายชื่อ
// ไม่แน่นอน ต่างจาก G-5/G-6 ที่สูงคงที่ RANKED_LIST_CARD_HEIGHT เสมอ (default, ไม่ใส่ prop นี้)
const RankedCustomerList = ({ label, hint, entries, color, emptyLabel, devNum, devName, onOpenProject, autoHeight, labelColor, hintColor }) => (
  <Card
    sx={{
      position: 'relative',
      p: 2,
      border: '1px solid #e1e0d9',
      boxShadow: 'none',
      height: autoHeight ? 'auto' : RANKED_LIST_CARD_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <DevCardBadge num={devNum} name={devName} />
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: labelColor }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.68rem', color: hintColor || TEXT_SECONDARY, mb: 1 }}>{hint}</Typography>
    {entries.length ? (
      <Box sx={autoHeight ? { pr: 1 } : { flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
        <Stack spacing={0.75}>
          {entries.map(([name, value, monYear, jobCount, jobs], i) => (
            <Tooltip
              key={name}
              placement="top"
              arrow
              title={
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, mb: 0.5 }}>{name}</Typography>
                  <JobBreakdownList jobs={jobs} total={value} onOpenProject={onOpenProject} />
                </Box>
              }
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ cursor: 'default' }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: color,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography sx={{ fontSize: '0.75rem', flex: 1, color: '#0b0b0b' }} noWrap>
                  {name}
                </Typography>
                {monYear && (
                  <Typography sx={{ fontSize: '0.66rem', color: AXIS_INK, flexShrink: 0, width: 32, textAlign: 'right' }}>
                    {monYear}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.66rem', color: AXIS_INK, flexShrink: 0, width: 22, textAlign: 'right' }}>
                  ×{jobCount}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY, flexShrink: 0, minWidth: 62, textAlign: 'right' }}>
                  {fmtBaht(value)}
                </Typography>
              </Stack>
            </Tooltip>
          ))}
        </Stack>
      </Box>
    ) : (
      <Typography sx={{ fontSize: '0.8rem', color: AXIS_INK, py: 4, textAlign: 'center' }}>
        {emptyLabel}
      </Typography>
    )}
  </Card>
);

export default function POOverviewTab({ rows: allRows, onOpenProject }) {
  const L = useL();
  // โครงการที่ยกเลิก (work_status='cancelled') ไม่นับในภาพรวม PO ทั้งแท็บ (ทุกการ์ด/กราฟ) — กรองที่ต้นทางจุดเดียว
  const rows = useMemo(
    () => (allRows || []).filter((r) => r?.work_status !== 'cancelled'),
    [allRows],
  );

  const [customerSort, setCustomerSort] = useState('amount'); // 'amount' | 'latest'
  const [companyMetric, setCompanyMetric] = useState('profit'); // 'profit' | 'bgPct' — G-10
  const [g4ShowAccumulated, setG4ShowAccumulated] = useState(false);
  const [g4Period, setG4Period] = useState('year'); // 'year' | 'month'
  const g4MonthClickTimerRef = useRef(null); // timer คลิกเดียวของแท่งรายเดือน (ให้ดับเบิลคลิกยกเลิกได้)
  const g4MonthScrollRef = useRef(null); // ตำแหน่ง scrollLeft ของกราฟรายเดือน (null = ยังไม่เคยเลื่อน)

  // apexcharts chart.events.dblClick ไม่ยิง event จริงบนแท่ง distributed bar (ลองแล้วไม่ทำงาน) —
  // ตรวจจับ double-click เองจากจังหวะคลิก 2 ครั้งติดกันภายใน 400ms บน dataPointIndex เดียวกัน
  // แทน โดยฟังจาก dataPointSelection ที่ยิงแน่นอนทุกครั้งที่คลิกโดนแท่ง — ใช้ ref เดียวกันข้ามกราฟ
  // (G-3/G-7/G-8/G-9) ได้เพราะแต่ละ handler เช็ค key ของตัวเองอยู่แล้ว ไม่มีทางชนกันจริง
  const lastBarClickRef = useRef({ id: null, time: 0 });

  // popover breakdown รายโปรเจกต์ของแท่ง "PO by customer" (G-3) — เปิดตอน double-click แท่ง
  // (แท่งรวมหลายโปรเจกต์ต่อคู่ค้า เลย double-click เปิด T-1 ตรงๆ แบบ G-7/8/9 ไม่ได้ ต้องโชว์ลิสต์
  // ให้เลือกก่อน) null = ปิดอยู่
  const [customerPopover, setCustomerPopover] = useState(null);

  // เปิดกราฟ G-3/G-4/G-11 เดี่ยวๆ แบบเต็มจอ (ดูปุ่ม OpenInFullIcon ที่มุมบนขวาของแต่ละการ์ด และ
  // Dialog ท้ายไฟล์) — เก็บแค่ id เดียวว่ากำลังขยายอันไหนอยู่ ('g3' | 'g4' | 'g11' | null) ไม่ต้องมี
  // state แยกต่อการ์ด เพราะเปิดได้ทีละอันพอ
  const [expandedChart, setExpandedChart] = useState(null);

  const {
    totalPo,
    projectCount,
    bigEntries,
    othersEntry,
    byYear,
    idByYear,
    yearAxisMax,
    lastYearToDatePo,
    lastYearToDateCount,
    yoyLastYear,
    yoyDiff,
    topProfit,
    topOverBudget,
    topLoss,
    retentionYear,
    activeCustomers,
    churnedCustomers,
    lawsuitCustomers,
    totalCustomerCount,
    companyProfitList,
    yearlyCumulativeProfit,
    yearMonthJobsMap,
    byMonth,
  } = useMemo(() => {
    // ขอบเขตทั้งแท็บนี้: เฉพาะโครงการ JS_ (ตาม convention กลุ่ม JS_/JSID_/อื่นๆ ใน utils.js)
    // และเฉพาะปี 2025 เป็นต้นไป (ตามวันที่รับงาน oldest_job_date/createdAt) — กรองที่ต้นทาง
    // ตรงนี้จุดเดียว ทุกกราฟ/การ์ดในแท็บนี้ (customer breakdown, year trend, top-10 x3, การ์ด
    // สรุปบนสุด) จึงอยู่ในขอบเขตเดียวกันหมด ไม่ต้องกรองซ้ำที่จุดอื่น
    const list = (rows || []).filter((r) => {
      if (!/^JS_/i.test(r?.project_number || '')) return false;
      const jd = jobDate(r);
      return jd && jd.getFullYear() >= YEAR_TREND_MIN;
    });
    let total = 0;
    const customerMap = new Map();
    const yearMap = new Map();
    // G-4 เท่านั้น: งาน ID ใช้ปีจากรหัส Job โดยตรง เช่น JSID_25... = 2025
    // ไม่รวมเข้า scope JS_ ของกราฟอื่นในแท็บนี้
    const idYearMap = new Map();
    for (const r of rows || []) {
      const match = String(r?.project_number || '').match(/^JSID_(\d{2})/i);
      if (!match) continue;
      const yr = 2000 + Number(match[1]);
      if (yr < YEAR_TREND_MIN) continue;
      const prev = idYearMap.get(yr) || { sum: 0, count: 0, jobs: [] };
      const paid = getCommittedCostValue(r) || 0;
      idYearMap.set(yr, {
        sum: prev.sum + paid,
        count: prev.count + 1,
        jobs: [
          ...prev.jobs,
          {
            code: r.project_number || '-',
            name: r.name || r.project_number || '-',
            value: paid,
            id: r._id || r.id,
            date: jobDate(r),
            customerName: customerName(r, L),
          },
        ],
      });
    }
    // สะสมยอด PO ต่อคู่ค้าแยกตามปี (ไม่ผ่านเกณฑ์ "อื่นๆ"/แท่งเดี่ยวแบบ customerMap ด้านล่าง —
    // สะสมทุกแถวเสมอ) ไว้ใช้แยก "คู่ค้าที่สั่งจ้างปีนี้" / "คู่ค้าเก่าที่เงียบปีนี้" ด้านล่างสุด
    const customerYearMap = new Map();
    // โครงการเดี่ยว PO <= เกณฑ์ "อื่นๆ" แต่งานยังไม่เสร็จ 100% — ห้ามยุบรวมเข้าก้อนของคู่ค้าหรือ
    // "อื่นๆ" เด็ดขาด ต้องขึ้นเป็นแท่งของตัวเอง (1 แท่ง = 1 โครงการ) ปนอยู่ในกราฟ "PO by customer"
    // เดิมเลย ไม่ใช่กราฟแยก — เลย skip ไม่เข้า customerMap ด้านล่าง
    const soloEntries = [];

    // เส้นแดง YoY: ยอดสะสม 1 ม.ค. ถึง "วันนี้ของปีที่แล้ว" (เทียบ apples-to-apples กับแท่งปีปัจจุบัน
    // ที่ก็เป็นยอดสะสม YTD อยู่แล้วโดยธรรมชาติ เพราะปียังไม่จบ)
    const today = new Date();
    const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
    const lastYearToDateCutoff = new Date(
      today.getFullYear() - 1,
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    );
    let lastYearToDatePo = 0;
    let lastYearToDateCount = 0;

    for (const r of list) {
      const po = getPoNetOfVat(r) || 0;
      total += po;

      const jd = jobDate(r);
      const cust = customerName(r, L);
      if (jd) {
        const yr = jd.getFullYear();
        const q = jobQuarter(jd);
        const prevYr = yearMap.get(yr) || { sum: 0, count: 0, quarters: [0, 0, 0, 0], jobs: [] };
        const quarters = [...prevYr.quarters];
        quarters[q] += po;
        yearMap.set(yr, {
          sum: prevYr.sum + po,
          count: prevYr.count + 1,
          quarters,
          jobs: [
            ...prevYr.jobs,
            {
              code: r.project_number || '-',
              name: r.name || r.project_number || '-',
              value: po,
              profit: getEstimatedProfit(r),
              id: r._id || r.id,
              date: jd,
              quarter: q,
              customerName: cust,
            },
          ],
        });

        const cyMap = customerYearMap.get(cust) || new Map();
        const cyPrev = cyMap.get(yr) || { sum: 0, count: 0, latest: null, jobs: [] };
        cyMap.set(yr, {
          sum: cyPrev.sum + po,
          count: cyPrev.count + 1,
          latest: !cyPrev.latest || jd > cyPrev.latest ? jd : cyPrev.latest,
          // รายละเอียดแต่ละ job ไว้โชว์ใน tooltip ตอน hover แถวคู่ค้า (G-5/G-6) — เก็บทั้งรหัสและ
          // ชื่อโครงการแยกกัน ให้คลิกสลับโหมดแสดงผลได้ (ดู RankedCustomerList ด้านบน), id ไว้เปิด
          // T-1 popup ตอน double-click, date ไว้โชว์คอลัมน์ Mo/Yr + เรียงตามวันที่ (เฉพาะโหมดรหัส),
          // profit ไว้ใช้แทน value(PO) เฉพาะ popover ของ G-10 (กำไร/ขาดทุนรายบริษัท ไม่ใช่ยอด PO),
          // budget(BG) ไว้โชว์คอลัมน์ BG ก่อนกำไร/ขาดทุนในป็อปอัพ G-10 เท่านั้น
          jobs: [
            ...cyPrev.jobs,
            {
              code: r.project_number || '-',
              name: r.name || r.project_number || '-',
              value: po,
              profit: getEstimatedProfit(r),
              budget: Number(r?.total_budget) || 0,
              id: r._id || r.id,
              date: jd,
            },
          ],
        });
        customerYearMap.set(cust, cyMap);
      }

      if (jd && jd >= lastYearStart && jd <= lastYearToDateCutoff) {
        lastYearToDatePo += po;
        lastYearToDateCount += 1;
      }

      if (po <= OTHERS_THRESHOLD && r?.work_status !== 'done') {
        const d = jobDate(r);
        soloEntries.push([
          `${customerName(r, L)} · ${r.project_number || '-'}`,
          po,
          fmtMonYear(d),
          d ? d.getTime() : 0,
          po, // "ต้นทุนโครงการล่าสุด" ของแท่งเดี่ยวก็คือ po ของมันเอง (มีแค่โครงการเดียว)
          true, // hasIncomplete — จริงโดยนิยามของเงื่อนไขที่กรองมา
          !!r?.legal_lawsuit,
          // จ๊อบเดียวอยู่แล้ว (แท่งเดี่ยว) — ไว้เปิด popover เดียวกับแท่งคู่ค้าจริงตอน double-click
          // แท่งใน G-3 (ดู JobBreakdownList/customerPopover ด้านล่าง)
          [
            {
              code: r.project_number || '-',
              name: r.name || r.project_number || '-',
              value: po,
              id: r._id || r.id,
              date: d,
              customerName: cust,
            },
          ],
        ]);
        continue;
      }

      const d = jobDate(r);
      const prev = customerMap.get(cust) || {
        sum: 0,
        latest: null,
        latestCost: 0,
        hasIncomplete: false,
        hasLawsuit: false,
      };
      const isNewLatest = d && (!prev.latest || d > prev.latest);
      customerMap.set(cust, {
        sum: prev.sum + po,
        latest: isNewLatest ? d : prev.latest,
        latestCost: isNewLatest ? po : prev.latestCost, // ต้นทุน PO ของ "โครงการล่าสุด" เพียงงานเดียว
        // true ถ้ามีอย่างน้อย 1 โครงการ (ที่เหลือ ไม่นับที่แยกเป็นแท่งเดี่ยวไปแล้ว) เข้าเงื่อนไข
        hasIncomplete: prev.hasIncomplete || r?.work_status !== 'done',
        hasLawsuit: prev.hasLawsuit || !!r?.legal_lawsuit,
      });
    }

    // G-11: เส้นเทียบปี — ยอดกำไรสะสมรายเดือนของแต่ละปี (2025 เป็นต้นไป เพราะ list กรองไว้แล้วตั้ง
    // แต่ต้น) แยก pass ต่างหากจาก companyProfitMap ด้านบน (นี่สะสมตามเดือน/ปี ไม่ใช่ตามบริษัท)
    const yearProfitMonthMap = new Map(); // year -> [ยอดกำไรของแต่ละเดือน 12 ช่อง]
    // เก็บ job รายตัวคู่กันไปด้วย ("yr-mo" -> jobs[]) ไว้ให้ double-click จุดในกราฟ G-11 เปิด
    // popover breakdown ว่ากำไรเดือนนั้นๆ ของปีนั้นๆ มาจากโครงการไหนบ้าง (เหมือน G-3/G-10)
    const yearMonthJobsMap = new Map();
    for (const r of list) {
      const jd = jobDate(r);
      if (!jd) continue;
      const yr = jd.getFullYear();
      const mo = jd.getMonth();
      const arr = yearProfitMonthMap.get(yr) || new Array(12).fill(0);
      const profit = getEstimatedProfit(r);
      arr[mo] += profit;
      yearProfitMonthMap.set(yr, arr);
      const jobKey = `${yr}-${mo}`;
      const jobs = yearMonthJobsMap.get(jobKey) || [];
      jobs.push({
        code: r.project_number || '-',
        name: r.name || r.project_number || '-',
        value: profit,
        id: r._id || r.id,
        date: jd,
        customerName: customerName(r, L),
      });
      yearMonthJobsMap.set(jobKey, jobs);
    }
    // G-4 แท็บรายเดือน: รวม PO/กำไร/ID PAID ต่อเดือน ตั้งแต่ YEAR_TREND_MIN-01 ถึงเดือนปัจจุบัน
    // (เติมเดือนที่ไม่มีข้อมูลเป็น 0 เพื่อให้แกนต่อเนื่อง) — ID ใช้เดือนจาก jobDate เหมือนปี
    const monthAgg = new Map();
    const bumpMonth = (d, patch) => {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const prev = monthAgg.get(key) || { po: 0, profit: 0, id: 0, count: 0, jobs: [], idJobs: [] };
      monthAgg.set(key, {
        po: prev.po + (patch.po || 0),
        profit: prev.profit + (patch.profit || 0),
        id: prev.id + (patch.id || 0),
        count: prev.count + (patch.count || 0),
        jobs: patch.job ? [...prev.jobs, patch.job] : prev.jobs,
        idJobs: patch.idJob ? [...prev.idJobs, patch.idJob] : prev.idJobs,
      });
    };
    const monthJob = (r, jd, value) => ({
      code: r.project_number || '-',
      name: r.name || r.project_number || '-',
      value,
      id: r._id || r.id,
      date: jd,
      customerName: customerName(r, L),
    });
    for (const r of list) {
      const jd = jobDate(r);
      if (jd) {
        const po = getPoNetOfVat(r) || 0;
        const profit = getEstimatedProfit(r);
        bumpMonth(jd, { po, profit, count: 1, job: { ...monthJob(r, jd, po), profit } });
      }
    }
    for (const r of rows || []) {
      const match = String(r?.project_number || '').match(/^JSID_(\d{2})/i);
      const jd = match && 2000 + Number(match[1]) >= YEAR_TREND_MIN ? jobDate(r) : null;
      if (jd) {
        const paid = getCommittedCostValue(r) || 0;
        bumpMonth(jd, { id: paid, idJob: monthJob(r, jd, paid) });
      }
    }
    const byMonth = [];
    for (let y = YEAR_TREND_MIN, m = 0; y < today.getFullYear() || (y === today.getFullYear() && m <= today.getMonth()); ) {
      const key = `${y}-${String(m + 1).padStart(2, '0')}`;
      byMonth.push([key, monthAgg.get(key) || { po: 0, profit: 0, id: 0, count: 0, jobs: [], idJobs: [] }]);
      m += 1;
      if (m > 11) {
        m = 0;
        y += 1;
      }
    }

    // entry: [ปี, [กำไรสะสม ณ สิ้นเดือนนั้นๆ 12 ช่อง]] เรียงปีเก่า→ใหม่
    const yearlyCumulativeProfit = [...yearProfitMonthMap.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([yr, monthly]) => {
        let running = 0;
        return [yr, monthly.map((v) => (running += v))];
      });

    // รวม job ทุกปีของคู่ค้าคนหนึ่งเป็น array เดียว (ใช้กับ G-3 popover และ G-10 popover — ต่างจาก
    // G-5/G-6 ที่แยกปีนี้/ปีก่อนหน้าอยู่แล้ว) ติด customerName กำกับทุก job ไว้เผื่อแท่ง "อื่นๆ" ใน
    // G-3 ที่รวมหลายคู่ค้าปนกัน (ดู othersEntryVal ด้านล่าง) — แท่งคู่ค้าเดี่ยวๆ ไม่ได้ใช้ field นี้
    const flattenCustomerJobs = (custName) => {
      const cyMap = customerYearMap.get(custName);
      if (!cyMap) return [];
      let all = [];
      for (const v of cyMap.values()) all = all.concat(v.jobs);
      return all.map((j) => ({ ...j, customerName: custName })).sort((a, b) => b.value - a.value);
    };

    // กำไร/ขาดทุนรวมต่อบริษัท (G-10) — คนละมิติกับ customerMap ด้านบน (นั่นสะสม PO ไม่ใช่กำไร)
    // เลยแยก pass ต่างหาก ไม่ต้องมีเงื่อนไขแยกแท่งเดี่ยว/"อื่นๆ" แบบ customerMap เพราะ G-10 ไม่ผูก
    // กับกราฟ PO by customer เลย — ทุกบริษัทเข้าเงื่อนไขเดียวกันหมด
    const companyProfitMap = new Map();
    for (const r of list) {
      const cust = customerName(r, L);
      const profit = getEstimatedProfit(r);
      const budget = Number(r?.total_budget) || 0;
      const po = getPoNetOfVat(r) || 0;
      const jd = jobDate(r);
      const prev = companyProfitMap.get(cust) || { profit: 0, budget: 0, poTotal: 0, latest: null };
      companyProfitMap.set(cust, {
        profit: prev.profit + profit,
        budget: prev.budget + budget,
        poTotal: prev.poTotal + po, // มูลค่าซื้อขายรวมกับเรา — ใช้กรอง "บริษัทขาดทุนที่ต้องติดกราฟเสมอ"
        latest: !prev.latest || (jd && jd > prev.latest) ? jd : prev.latest,
      });
    }
    // entry: [ชื่อบริษัท, กำไร/ขาดทุนรวม, งบ(BG)รวม, timestamp งานล่าสุด (ไว้เรียงตามเวลาที่เกิด),
    //         "MM/YY" งานล่าสุด, มูลค่าซื้อขายรวม, รายละเอียดแต่ละ job (ไว้เปิด popover ตอน
    //         double-click แท่ง — เหมือน G-3)]
    const companyProfitList = [...companyProfitMap.entries()].map(([name, v]) => [
      name,
      v.profit,
      v.budget,
      v.latest ? v.latest.getTime() : 0,
      fmtMonYear(v.latest),
      v.poTotal,
      // popover ของ G-10 โชว์ "กำไร/ขาดทุน" ต่อ job ไม่ใช่ยอด PO เหมือน G-3 — สลับ value ให้เป็น
      // profit ของแต่ละ job แทน (jobs ดิบจาก flattenCustomerJobs มี value=PO ไว้ใช้กับ G-3/G-5/G-6
      // อยู่แล้ว ไม่แตะของเดิม แค่แม็ปใหม่เฉพาะจุดนี้) — เก็บ po ไว้แยกก่อนทับ value ไม่งั้นยอด PO
      // เดิมหายไปเลย (ใช้โชว์คอลัมน์ PO คู่กับ BG ใน popup)
      flattenCustomerJobs(name)
        .map((j) => ({ ...j, po: j.value, value: j.profit }))
        .sort((a, b) => b.value - a.value),
    ]);

    // การ์ด "คู่ค้าที่สั่งจ้างปีนี้" (ยอดปีนี้ > 1 แสนบาท) / "คู่ค้าเก่าที่เงียบปีนี้" (เคยยอด
    // > 1 แสนบาทในปีก่อนหน้าที่อยู่ใน scope คือ 2025..ปีนี้-1 แต่ปีนี้ไม่มีการสั่งจ้างเลย) — ทั้งคู่
    // อิงจาก customerYearMap ที่สะสมไว้ข้างบน (ทุกคู่ค้าใน list ไม่ว่าจะโดนแยกแท่งเดี่ยว/"อื่นๆ"
    // ในกราฟ PO by customer หรือไม่ก็ตาม) — scope ปี 2025 เป็นต้นไปเดียวกับทั้งแท็บ ไม่นับย้อนไปก่อน
    // 2025 (ตามที่ยืนยันไว้)
    // entry: [ชื่อคู่ค้า, มูลค่า, "MM/YY" ของงานล่าสุด (ในขอบเขตที่นับ), จำนวน job ที่รวมอยู่ในยอดนี้,
    //         รายละเอียดแต่ละ job [{label, value}] เรียงมูลค่ามากไปน้อย — ไว้โชว์ tooltip ตอน hover]
    const retentionCurrentYear = today.getFullYear();
    const activeCustomersList = [];
    const churnedCustomersList = [];
    for (const [cust, cyMap] of customerYearMap.entries()) {
      const thisYear = cyMap.get(retentionCurrentYear);
      const thisYearSum = thisYear?.sum || 0;
      if (thisYearSum > CUSTOMER_RETENTION_THRESHOLD) {
        const jobs = [...thisYear.jobs].sort((a, b) => b.value - a.value);
        activeCustomersList.push([cust, thisYearSum, fmtMonYear(thisYear.latest), thisYear.count, jobs]);
        continue;
      }
      if (thisYearSum > 0) continue; // มีสั่งจ้างปีนี้อยู่บ้าง แค่ไม่ถึงเกณฑ์ — ไม่นับเป็น "เงียบไปเลย"
      let pastSum = 0;
      let pastCount = 0;
      let pastLatest = null;
      let pastJobs = [];
      for (const [yr2, v] of cyMap.entries()) {
        if (yr2 === retentionCurrentYear) continue;
        pastSum += v.sum;
        pastCount += v.count;
        pastJobs = pastJobs.concat(v.jobs);
        if (!pastLatest || (v.latest && v.latest > pastLatest)) pastLatest = v.latest;
      }
      if (pastSum > CUSTOMER_RETENTION_THRESHOLD) {
        pastJobs.sort((a, b) => b.value - a.value);
        churnedCustomersList.push([cust, pastSum, fmtMonYear(pastLatest), pastCount, pastJobs]);
      }
    }
    activeCustomersList.sort((a, b) => b[1] - a[1]);
    churnedCustomersList.sort((a, b) => b[1] - a[1]);

    // G-12: ลูกค้าที่ถูกฟ้องร้อง — สะสม "มูลค่าการฟ้องร้อง" ต่อคู่ค้าเฉพาะโครงการที่
    // legal_lawsuit = true เท่านั้น มูลค่าการฟ้องร้อง = ยอดเงินที่ยังไม่ได้รับ = PO สุทธิ
    // (getPoNetOfVat) - รับเงินแล้ว (received + seed_received) — สูตรเดียวกับที่ getEstimatedProfit
    // ใน formulas.js ใช้คำนวณ "รับเงินมาแล้ว" สำหรับโครงการที่ถูกฟ้องร้อง แยก pass ต่างหาก ไม่ใช้
    // customerYearMap ตรงๆ เพราะตัวนั้นรวมทุกโครงการ ไม่กรองเฉพาะที่ถูกฟ้องร้อง
    const lawsuitMap = new Map();
    for (const r of list) {
      if (!r?.legal_lawsuit) continue;
      const cust = customerName(r, L);
      const receivedCombined = (Number(r?.received) || 0) + (Number(r?.seed_received) || 0);
      const unreceived = (getPoNetOfVat(r) || 0) - receivedCombined;
      const d = jobDate(r);
      const prev = lawsuitMap.get(cust) || { sum: 0, latest: null, jobs: [] };
      lawsuitMap.set(cust, {
        sum: prev.sum + unreceived,
        latest: !prev.latest || (d && d > prev.latest) ? d : prev.latest,
        jobs: [
          ...prev.jobs,
          { code: r.project_number || '-', name: r.name || r.project_number || '-', value: unreceived, id: r._id || r.id, date: d },
        ],
      });
    }
    const lawsuitCustomersList = [...lawsuitMap.entries()]
      .map(([name, v]) => [
        name,
        v.sum,
        fmtMonYear(v.latest),
        v.jobs.length,
        [...v.jobs].sort((a, b) => b.value - a.value),
      ])
      .sort((a, b) => b[1] - a[1]);

    // entry: [name, sum, "(MM/YY)" ของวันที่ล่าสุด, timestamp ของวันที่ล่าสุด (ไว้เรียง),
    //         ต้นทุน PO ของโครงการล่าสุดเพียงงานเดียว, มีโครงการยังไม่เสร็จ100%?, มีคดีฟ้องร้อง?,
    //         รายละเอียดแต่ละ job — ไว้เปิด popover ตอน double-click แท่งใน G-3]
    const toEntry = ([name, { sum, latest, latestCost, hasIncomplete, hasLawsuit }]) => [
      name,
      sum,
      fmtMonYear(latest),
      latest ? latest.getTime() : 0,
      latestCost,
      hasIncomplete,
      hasLawsuit,
      flattenCustomerJobs(name),
    ];
    const customerEntries = [...customerMap.entries()];
    // ไม่เรียงตรงนี้ — ปล่อยให้ derive ตาม customerSort ทีหลัง (จะได้สลับปุ่มได้โดยไม่ต้อง
    // aggregate ใหม่) — รวมแท่งเดี่ยว (soloEntries) เข้าพูลเดียวกับแท่งคู่ค้าปกติเลย
    const bigEntriesList = [
      ...customerEntries.filter(([, { sum }]) => sum > OTHERS_THRESHOLD).map(toEntry),
      ...soloEntries,
    ];
    const smallEntries = customerEntries.filter(([, { sum }]) => sum <= OTHERS_THRESHOLD);

    let othersEntryVal = null;
    if (smallEntries.length) {
      const restSum = smallEntries.reduce((s, [, { sum }]) => s + sum, 0);
      let restLatest = null;
      let restLatestCost = 0;
      let restHasIncomplete = false;
      let restHasLawsuit = false;
      for (const [, v] of smallEntries) {
        if (v.latest && (!restLatest || v.latest > restLatest)) {
          restLatest = v.latest;
          restLatestCost = v.latestCost; // ต้นทุนของโครงการล่าสุดสุดในกลุ่มคู่ค้ารายเล็กทั้งหมด
        }
        restHasIncomplete = restHasIncomplete || v.hasIncomplete;
        restHasLawsuit = restHasLawsuit || v.hasLawsuit;
      }
      othersEntryVal = [
        L(`อื่นๆ (${smallEntries.length} ราย)`, `Others (${smallEntries.length})`),
        restSum,
        fmtMonYear(restLatest),
        restLatest ? restLatest.getTime() : 0,
        restLatestCost,
        restHasIncomplete,
        restHasLawsuit,
        // รวม job ของทุกคู่ค้ารายเล็กในกลุ่มนี้ (ติด customerName กำกับ เพราะมีหลายบริษัทปนกัน —
        // popover ของแท่งนี้ต้องโชว์ชื่อบริษัทต่อแถวด้วย ต่างจากแท่งคู่ค้าเดี่ยวๆ)
        smallEntries.flatMap(([custName]) => flattenCustomerJobs(custName)).sort((a, b) => b.value - a.value),
      ];
    }

    // entry: [year, sum, จำนวนจ๊อบของปีนั้น, [Q1,Q2,Q3,Q4], jobs]
    const chartYears = new Set([...yearMap.keys(), ...idYearMap.keys()]);
    const byYearList = [...chartYears]
      .filter((y) => y >= YEAR_TREND_MIN)
      .sort((a, b) => a - b)
      .map((y) => {
        const { sum = 0, count = 0, quarters = [0, 0, 0, 0], jobs = [] } = yearMap.get(y) || {};
        return [y, sum, count, quarters, jobs];
      });

    // เผื่อ max ไว้ให้พอสำหรับเส้นแดง YoY ด้วย เผื่อปีที่แล้ว ณ จุดเดียวกันสูงกว่าทุกแท่งตอนนี้ —
    // ป้าย YoY ย้ายออกไปเป็น header คงที่แล้ว เหลือแค่ป้ายยอดรวมเล็กๆ ลอยเหนือแท่ง เผื่อ 12% ก็พอ
    // (ไม่ใช่ 30% เหมือนก่อนหน้านี้ที่เผื่อไว้สำหรับป้าย YoY ที่ไม่มีแล้ว)
    const yearMax = Math.max(
      byYearList.reduce((m, [, v]) => Math.max(m, v), 0),
      [...idYearMap.values()].reduce((m, v) => Math.max(m, v.sum), 0),
      lastYearToDatePo,
    );

    // diff ของปีปัจจุบัน (ปีนี้ = ตัว "YTD จริง" อยู่แล้วเพราะปียังไม่จบ) เทียบกับเส้น YoY —
    // คำนวณจาก "ปีที่แล้ว" ตรงๆ (today.getFullYear()-1) ไม่ผูกกับ index/จำนวนแท่งในกราฟ ปีหน้า
    // เส้นนี้จะขยับไปเทียบปีนี้แทนเองอัตโนมัติ ไม่ว่ากราฟจะมีกี่แท่งก็ตาม
    const yoyCurrentYear = today.getFullYear();
    const yoyLastYear = today.getFullYear() - 1;
    const currentYearSum = byYearList.find(([y]) => y === yoyCurrentYear)?.[1] || 0;
    const yoyDiff = currentYearSum - lastYearToDatePo;

    // entry ร่วมของ 3 การ์ด top-10: [label "JS_xxxxx", value, ชื่อคู่ค้า, ชื่อโครงการเต็ม,
    // "(MM/YY)" ของวันที่รับงาน, timestamp วันที่รับงาน (ไว้เรียงลำดับที่แสดงตามจ๊อบก่อน-หลัง),
    // มีคดีฟ้องร้อง? (การ์ด "ขาดทุนสูงสุด" ใช้ตัวนี้ทาสีเลือดหมูแยกจากขาดทุนปกติ), project id
    // (ไว้เปิด T-1 popup ตอน double-click แท่ง)
    const toTopEntry = ([r, value]) => {
      const d = jobDate(r);
      return [
        r.project_number || '-',
        value,
        customerName(r, L),
        r.name || '',
        fmtMonYear(d),
        d ? d.getTime() : 0,
        !!r?.legal_lawsuit,
        r._id || r.id,
      ];
    };
    // เลือกว่าใครติด top-10 ตามเมตริก (กำไร/เกิน budget/ขาดทุน) ก่อน แล้วค่อยจัดลำดับที่จะโชว์บน
    // แกน x ใหม่ตามวันที่รับงาน (จ๊อบไหนมาก่อนอยู่ซ้ายก่อน) — คนละขั้นตอนกับการคัดว่าใครติด top-10
    const byJobDateAsc = (a, b) => a[5] - b[5];

    // "กำไรสูงสุด"/"ขาดทุนสูงสุด" ใช้เมตริกเดียวกัน (getEstimatedProfit สูตรเดียวกับ P5/P111 ใน
    // formulas.js) แค่เรียงคนละทิศ — คำนวณครั้งเดียวต่อโครงการแล้วแชร์กันทั้งสองการ์ด
    const withProfit = list.map((r) => [r, getEstimatedProfit(r)]);
    const topProfitList = [...withProfit]
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_LIST_COUNT)
      .map(toTopEntry)
      .sort(byJobDateAsc);
    const topLossList = [...withProfit]
      .sort((a, b) => a[1] - b[1])
      .slice(0, TOP_LIST_COUNT)
      .map(toTopEntry)
      .sort(byJobDateAsc);

    // "เกิน budget มากที่สุด" = (จ่ายแล้ว + รอจ่าย) - budget = -getProjectedFund (ซึ่ง =
    // budget - จ่ายแล้ว - รอจ่าย อยู่แล้วใน formulas.js — กลับเครื่องหมายตรงๆ)
    const topOverBudgetList = list
      .map((r) => [r, -getProjectedFund(r)])
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_LIST_COUNT)
      .map(toTopEntry)
      .sort(byJobDateAsc);

    return {
      totalPo: total,
      projectCount: list.length,
      bigEntries: bigEntriesList,
      othersEntry: othersEntryVal,
      byYear: byYearList,
      idByYear: idYearMap,
      yearAxisMax: yearMax * 1.15 || undefined,
      lastYearToDatePo,
      lastYearToDateCount,
      yoyLastYear,
      yoyDiff,
      topProfit: topProfitList,
      topOverBudget: topOverBudgetList,
      topLoss: topLossList,
      retentionYear: retentionCurrentYear,
      activeCustomers: activeCustomersList,
      churnedCustomers: churnedCustomersList,
      lawsuitCustomers: lawsuitCustomersList,
      // จำนวนคู่ค้าทั้งหมดที่มีโปรเจกต์ในขอบเขตของแท็บนี้ (JS_ + 2025 เป็นต้นไป) ไม่ผ่านเกณฑ์ 1
      // แสนบาทใดๆ — ไว้โชว์ "(จำนวนที่แสดง/ทั้งหมด)" ต่อท้ายป้าย G-5/G-6
      totalCustomerCount: customerYearMap.size,
      companyProfitList,
      yearlyCumulativeProfit,
      yearMonthJobsMap,
      byMonth,
    };
  }, [rows, L]);

  // "อื่นๆ" อยู่ท้ายแถว (ล่างสุดของกราฟ) เสมอ ไม่ว่าจะเรียงตามยอดหรือวันที่ล่าสุด — ไม่ปนเข้าไป
  // เรียงร่วมกับคู่ค้ารายชื่อจริง — apex วาดแนวนอนบนลงล่างตามลำดับ array ตรงๆ
  // โหมด "รับงานล่าสุด" ใช้ค่า "ต้นทุน PO ของโครงการล่าสุดงานเดียว" (index 4) แทนยอดรวมสะสม
  // (index 1) — กรองมาโชว์แค่งานล่าสุดของแต่ละคู่ค้า ไม่ใช่ผลรวมทั้งหมดอีกต่อไป
  const byCustomer = useMemo(() => {
    const toRender = (e) => [
      e[0],
      customerSort === 'latest' ? e[4] : e[1],
      e[2],
      e[3],
      e[5], // hasIncomplete
      e[6], // hasLawsuit
      e[7], // jobs — ไว้เปิด popover ตอน double-click แท่ง (G-3)
    ];
    const sorted = [...bigEntries].sort((a, b) =>
      customerSort === 'latest' ? b[3] - a[3] : b[1] - a[1],
    );
    const mapped = sorted.map(toRender);
    return othersEntry ? [...mapped, toRender(othersEntry)] : mapped;
  }, [bigEntries, othersEntry, customerSort]);

  // G-10: กรองเอาเฉพาะบริษัทที่มูลค่าซื้อขายรวมกับเรา > 1 แสนบาทตั้งแต่ต้น (บริษัทจิ๊บจ๊อยไม่ต้อง
  // ขึ้นกราฟเลย) แล้ว "เรียงตามเวลาที่เกิด" คือตัวคัดว่าใครติดกราฟด้วย ไม่ใช่แค่ลำดับที่แสดง — เอา
  // 30 บริษัทที่มีโปรเจกต์ล่าสุด (latestTs) ใหม่สุดก่อน แล้วเรียงแกน x เก่า→ใหม่ (ซ้าย→ขวา)
  // เมตริกที่เลือก (กำไรสูงสุด/%กำไร-BG สูงสุด) กำหนดแค่ค่าที่พล็อตบนแกน y เท่านั้น ไม่ได้กำหนดว่า
  // ใครติดกราฟ — ยกเว้น "บริษัทขาดทุน" (ค่าตามเมตริกที่เลือกติดลบ) การันตีให้ติดกราฟเสมอไม่ว่าจะ
  // เก่าแค่ไหน (กันเคสขาดทุนจริงแต่เก่ากว่า 30 บริษัทอื่นแล้วหลุดกราฟไปเงียบๆ ตอนคัดด้วยความล่าสุด
  // อย่างเดียว) — ผ่านเกณฑ์ 1 แสนบาทตั้งแต่ต้นแล้วเลยไม่ต้องเช็ค poTotal ซ้ำตรงนี้อีก
  const companyProfitTop = useMemo(() => {
    const aboveThreshold = companyProfitList.filter(([, , , , , poTotal]) => poTotal > CUSTOMER_RETENTION_THRESHOLD);
    const withBgPct = aboveThreshold.map(([name, profit, budget, latestTs, latestLabel, poTotal, jobs]) => ({
      name,
      profit,
      budget,
      bgPct: budget > 0 ? (profit / budget) * 100 : null,
      latestTs,
      latestLabel,
      poTotal,
      jobs,
    }));
    // โหมด %กำไร/BG ต้องมี BG ตั้งไว้จริงถึงจะคำนวณสัดส่วนได้ — บริษัทที่ไม่มี BG เลยไม่มีอะไรให้
    // พล็อตในโหมดนี้ (bgPct = null ถูกกรองทิ้งก่อนคัดตามเวลา)
    const eligible = companyMetric === 'bgPct' ? withBgPct.filter((e) => e.bgPct != null) : withBgPct;
    const getVal = (e) => (companyMetric === 'bgPct' ? e.bgPct : e.profit);

    const forcedLoss = eligible.filter((e) => getVal(e) < 0);
    const rest = eligible.filter((e) => getVal(e) >= 0);

    let selected;
    if (forcedLoss.length >= COMPANY_CHART_COUNT) {
      // ขาดทุนเยอะจนล้นโควตาเอง — เอาที่ล่าสุดสุด COMPANY_CHART_COUNT ตัว (ยังให้สิทธิ์ขาดทุนก่อนกำไรอยู่)
      selected = [...forcedLoss].sort((a, b) => b.latestTs - a.latestTs).slice(0, COMPANY_CHART_COUNT);
    } else {
      const remainingSlots = COMPANY_CHART_COUNT - forcedLoss.length;
      const fillIn = [...rest].sort((a, b) => b.latestTs - a.latestTs).slice(0, remainingSlots);
      selected = [...forcedLoss, ...fillIn];
    }
    return selected.sort((a, b) => a.latestTs - b.latestTs);
  }, [companyProfitList, companyMetric]);

  // แท่งสีเขียว/แดงตามเครื่องหมายของค่า (กำไร = เขียว, ขาดทุน = แดง) — ต่างจาก G-7/8/9 ที่แยกการ์ด
  // ตามเมตริกไปเลย ตัวนี้รวมกำไร/ขาดทุนในกราฟเดียวกันตามที่ขอ เลยต้องไล่สีทีละแท่งเอง — เพิ่มเงื่อนไข
  // ปี: บริษัทที่โปรเจกต์ล่าสุดไม่ใช่ปีนี้ใช้สีทึบ/เข้มกว่า (แยกสายตาว่าอันไหนใหม่/เก่า)
  const currentYear = new Date().getFullYear();
  const companyProfitOptions = useMemo(() => {
    const barColors = companyProfitTop.map((e) => {
      const isProfit = (companyMetric === 'bgPct' ? e.bgPct : e.profit) >= 0;
      const isThisYear = e.latestTs && new Date(e.latestTs).getFullYear() === currentYear;
      if (isProfit) return isThisYear ? PROFIT_GREEN : PROFIT_GREEN_MUTED;
      return isThisYear ? LOSS_RED : LOSS_RED_MUTED;
    });
    return {
      // double-click เปิด popover breakdown รายโปรเจกต์ของบริษัทนั้น (เหมือน G-3) ผูกอยู่ที่
      // handleG10DoubleClick บนกล่องครอบกราฟแทน chart.events.dataPointSelection — ดูคอมเมนต์
      // ที่นิยาม handleG10DoubleClick ด้านล่างว่าทำไมต้องแยกออกมา
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'inherit',
      },
      plotOptions: {
        bar: {
          columnWidth: '55%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
          distributed: true,
          // ค่ากำไร/ขาดทุนใกล้ 0 ทำให้แท่งเตี้ยจนแทบไม่มีพื้นที่ให้ hover — เพิ่ม background bar
          // โปร่งใสสนิท (ไม่มีสี) เต็มความสูงกราฟต่อคอลัมน์ ให้ mousemove-tracking ของ apex (ตัวที่
          // ควบคุม tooltip) จับตำแหน่งได้ตลอดคอลัมน์ ไม่ใช่แค่ตรงแท่งเตี้ยๆ ที่มองเห็น — ส่วน
          // double-click (คลิกจริง ไม่ใช่แค่ hover) ไม่ผูกกับ background bar นี้เลย เพราะ apex ไม่ได้
          // เอา click listener มาผูกกับ element ตัวนี้ ต้องแยกไปดัก native dblclick เอง (ดู
          // handleG10DoubleClick ด้านล่าง)
          colors: { backgroundBarColors: ['rgba(0,0,0,0)'], backgroundBarOpacity: 1, backgroundBarRadius: 4 },
        },
      },
      colors: barColors,
      legend: { show: false },
      dataLabels: { enabled: false },
      grid: { borderColor: GRID, strokeDashArray: 0 },
      xaxis: {
        categories: companyProfitTop.map((e) => e.name),
        labels: { style: { colors: AXIS_INK, fontSize: '10px' }, rotate: -35, trim: true, maxHeight: 70 },
      },
      yaxis: {
        labels: {
          style: { colors: TEXT_SECONDARY, fontSize: '10px' },
          formatter: (v) => (companyMetric === 'bgPct' ? `${fmtBaht(v)}%` : fmtBaht(v)),
        },
      },
      tooltip: {
        intersect: false,
        shared: false,
        custom: ({ dataPointIndex }) => {
          const e = companyProfitTop[dataPointIndex];
          if (!e) return '';
          const profitColor = e.profit >= 0 ? PROFIT_GREEN : LOSS_RED;
          return `
            <div style="padding:8px 12px; font-family:inherit; max-width:220px;">
              <div style="font-size:11px; font-weight:600; color:#0b0b0b;">${e.name}</div>
              <div style="font-size:12px; font-weight:600; color:${profitColor};">${fmtBaht(e.profit)} ${L('บาท', 'THB')}</div>
              ${e.budget > 0 ? `<div style="font-size:11px; color:${TEXT_SECONDARY};">BG: ${fmtBaht(e.budget)} ${L('บาท', 'THB')} (${e.bgPct != null ? e.bgPct.toFixed(1) : '-'}%)</div>` : ''}
              ${e.latestLabel ? `<div style="font-size:11px; color:${AXIS_INK}; margin-top:4px;">${L('รับงานล่าสุด', 'Latest job')}&nbsp;&nbsp;&nbsp;<span style="color:${FADED_TEXT};">(${e.latestLabel})</span></div>` : ''}
            </div>
          `;
        },
      },
      // ป้ายตัวเลขลอยเหนือ/ใต้แท่งแยกจาก dataLabels ปกติ (ทั้งบวก/ลบปนกันในกราฟเดียว — เคยลอง
      // dataLabels ธรรมดาแล้วป้ายแท่งบวกไปทับแท่งลบเอง ดูคอมเมนต์ที่ buildTopListOptions ด้านบน)
      // ใช้ annotations.points วางเองทีละจุดแทน เหมือน "แนวโน้ม PO รายปี" — แก้ปัญหาสเกลกว้างจน
      // แท่งเล็กๆ มองแทบไม่เห็น เพราะตัวเลขยังอ่านได้เสมอไม่ว่าแท่งจะสูง/เตี้ยแค่ไหน
      annotations: {
        points: companyProfitTop.map((e) => {
          const val = companyMetric === 'bgPct' ? e.bgPct : e.profit;
          const isNeg = val < 0;
          const isThisYear = e.latestTs && new Date(e.latestTs).getFullYear() === currentYear;
          // ป้าย % (ไม่ใช่สีแท่ง) ที่เป็นบวก ในโหมด %กำไร/BG ให้สีอิงตามช่วงที่ตกอยู่เทียบกับเส้น
          // อ้างอิง 10/20/30% ด้านล่าง (แดง/ฟ้า/เขียว ตามช่วงที่ผ่านล่าสุด) แทนสีเขียวเดียวเดิม —
          // แท่งกราฟเองยังคงสีเขียว/แดงตามเครื่องหมายเหมือนเดิม ไม่เปลี่ยน, ค่าติดลบก็ยังใช้แดงเดิม
          const pctBracketColor =
            val >= 30 ? '#4ade80' : val >= 20 ? BLUE : '#fca5a5';
          const labelColor = isNeg
            ? isThisYear ? LOSS_RED : LOSS_RED_MUTED
            : companyMetric === 'bgPct'
            ? pctBracketColor
            : isThisYear ? PROFIT_GREEN : PROFIT_GREEN_MUTED;
          return {
            x: e.name,
            y: val,
            marker: { size: 0 },
            label: {
              borderWidth: 0,
              offsetY: isNeg ? 14 : -6,
              style: {
                color: labelColor,
                background: 'transparent',
                fontSize: '9px',
                fontWeight: 600,
              },
              text: companyMetric === 'bgPct' ? `${val.toFixed(1)}%` : fmtBaht(val),
            },
          };
        }),
        // เส้นอ้างอิง 10/20/30% เฉพาะโหมด %กำไร/BG เท่านั้น (โหมดกำไรบาทตรงๆ ไม่มีเกณฑ์ % ให้เทียบ)
        // คนละสีต่อเส้น: 10%=แดงอ่อน, 20%=ฟ้า, 30%=เขียวสว่าง
        yaxis:
          companyMetric === 'bgPct'
            ? [
                { pct: 10, color: '#fca5a5' },
                { pct: 20, color: BLUE },
                { pct: 30, color: '#4ade80' },
              ].map(({ pct, color }) => ({
                y: pct,
                borderColor: color,
                strokeDashArray: 4,
                label: {
                  borderColor: 'transparent',
                  style: { color, background: 'transparent', fontSize: '9px' },
                  text: `${pct}%`,
                },
              }))
            : [],
      },
    };
  }, [companyProfitTop, companyMetric, currentYear, L]);

  // G-10: apex ผูก dataPointSelection ไว้กับ path ของแท่งข้อมูลจริงเท่านั้น (element class
  // apexcharts-bar-area) ไม่ได้ผูกกับ background bar ที่เพิ่มไว้ให้โปร่งใสเต็มความสูงคอลัมน์ —
  // คลิกโดน background bar (พื้นที่ที่แท่งจริงเตี้ยจนไม่มีให้คลิก) เลยไม่ trigger เลย ทั้งที่มองด้วย
  // ตาเหมือนคลิกโดนคอลัมน์เดียวกัน แก้โดยดัก native onDoubleClick ที่กล่องครอบกราฟแทน คำนวณเอง
  // ว่าตำแหน่งคลิกอยู่ในคอลัมน์ไหนจากตำแหน่ง x เทียบกับกรอบ .apexcharts-grid (พื้นที่พล็อตจริง ไม่รวม
  // แถบชื่อแกน y) — ใช้ dblclick ของ browser ตรงๆ (แม่นกว่า self-timed 400ms แบบกราฟอื่น เพราะที่นี่
  // ไม่ต้องพึ่ง dataPointSelection มายิงให้เลย)
  const g10ChartWrapRef = useRef(null);
  const handleG10DoubleClick = (event) => {
    const wrap = g10ChartWrapRef.current;
    const count = companyProfitTop.length;
    if (!wrap || !count) return;
    const gridEl = wrap.querySelector('.apexcharts-grid');
    if (!gridEl) return;
    const rect = gridEl.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || rect.width <= 0) return;
    const idx = Math.min(count - 1, Math.max(0, Math.floor(((event.clientX - rect.left) / rect.width) * count)));
    const entry = companyProfitTop[idx];
    if (!entry) return;
    setCustomerPopover({
      anchorPosition: { top: event.clientY, left: event.clientX },
      label: entry.name,
      total: entry.profit,
      jobs: entry.jobs || [],
      isOthers: false,
      showBudget: true,
    });
  };

  // G-11: เส้นเทียบปี — 1 เส้นต่อ 1 ปี พล็อตกำไรสะสมรายเดือน (ม.ค.-ธ.ค.) ให้เทียบ pace ปีต่อปีได้
  // ด้วยตา — เส้นปีปัจจุบันหนากว่าเส้นปีอื่นเพื่อเน้น ส่วนสีวนตาม QUARTER_COLORS ตามลำดับปีเก่า→ใหม่
  const yearlyProfitLineOptions = useMemo(() => {
    const monthLabels = L(MONTH_LABELS_TH, MONTH_LABELS_EN);
    return {
      chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit' },
      stroke: {
        curve: 'smooth',
        width: yearlyCumulativeProfit.map(([yr]) => (yr === currentYear ? 3 : 2)),
      },
      colors: yearlyCumulativeProfit.map((_, i) => QUARTER_COLORS[i % QUARTER_COLORS.length]),
      markers: { size: 3 },
      dataLabels: { enabled: false },
      legend: { position: 'top', fontSize: '11px', labels: { colors: TEXT_SECONDARY } },
      // เผื่อที่ว่างด้านบนกริด 20px ไม่ให้ป้าย diff ที่ลอยเหนือจุดสูงสุด (offsetY: -8) โดนตัดขาดที่
      // ขอบบนของ SVG — ใช้ grid.padding แทนการขยับ yaxis.max เพราะวิธีนั้นเปลี่ยนสเกล/เส้นกริดทั้งกราฟ
      // ไปด้วย (ไม่ต้องการ) ส่วนนี้แค่เผื่อพื้นที่เปล่าด้านบน ไม่กระทบตำแหน่ง/สเกลของเส้นหรือแกน y เลย
      grid: { borderColor: GRID, strokeDashArray: 0, padding: { top: 20 } },
      xaxis: {
        // เพิ่ม 2 ช่องท้ายแกน x (ม.ค./ก.พ. ของปีถัดไป) แบบไม่มีข้อมูลจริงเลย (ดู series ใน JSX ที่
        // เติม null 2 ตัวท้ายให้ยาวเท่ากัน) — จุดประสงค์เดียวคือ "หลอก" เอาพื้นที่ว่างท้ายกราฟ กัน
        // จุดสุดท้ายจริง (ธ.ค.) ชิดขอบขวาสุดจนป้ายตัวเลขที่ลอยอยู่โดนตัดขอบ ไม่ได้ตั้งใจให้มีเส้น/
        // ข้อมูลอะไรไปวางในช่องที่เพิ่มนี้จริงๆ
        categories: [...monthLabels, monthLabels[0], monthLabels[1]],
        labels: { style: { colors: AXIS_INK, fontSize: '11px' } },
      },
      yaxis: {
        labels: {
          style: { colors: TEXT_SECONDARY, fontSize: '11px' },
          formatter: (v) => fmtBaht(v),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        // แสดงยอดของทุกปี ณ เดือนนั้น + diff จากเดือนก่อนหน้าต่อปี (กำไรที่เพิ่มขึ้นเฉพาะเดือนนั้น
        // ไม่ใช่ยอดสะสม) + diff ระหว่างปีล่าสุดกับปีก่อนหน้า — เดือนที่ยังไม่ถึงเป็น null อยู่แล้ว
        // จาก series ตอน build series (ดู JSX) เลย filter ทิ้งก่อนเสมอ ไม่งั้น diff จะเทียบกับ null
        custom: ({ series, dataPointIndex }) => {
          const years = yearlyCumulativeProfit.map(([yr]) => yr);
          const points = years
            .map((yr, seriesIdx) => ({ yr, seriesIdx, v: series[seriesIdx]?.[dataPointIndex] }))
            .filter((p) => p.v != null);
          if (!points.length) return '';
          const rowsHtml = points
            .map((p) => {
              const prevMonthVal = dataPointIndex > 0 ? series[p.seriesIdx]?.[dataPointIndex - 1] : null;
              // ม.ค. ไม่มีเดือนก่อนหน้าให้เทียบ — ยอดสะสม ณ ม.ค. ก็คือกำไรของเดือนนั้นเป๊ะๆ อยู่แล้ว
              const moDiff = dataPointIndex === 0 ? p.v : prevMonthVal != null ? p.v - prevMonthVal : null;
              const moDiffHtml =
                moDiff == null
                  ? ''
                  : ` <span style="color:${moDiff >= 0 ? PROFIT_GREEN : LOSS_RED};">(${moDiff >= 0 ? '+' : ''}${fmtBaht(moDiff)})</span>`;
              return `
                <div style="display:flex; justify-content:space-between; gap:12px; font-size:11px; color:${TEXT_SECONDARY};">
                  <span><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${QUARTER_COLORS[p.seriesIdx % QUARTER_COLORS.length]};margin-right:4px;"></span>${p.yr}</span>
                  <span style="font-weight:600; color:#0b0b0b;">${fmtBaht(p.v)}${moDiffHtml}</span>
                </div>
              `;
            })
            .join('');
          let diffHtml = '';
          if (points.length >= 2) {
            const latest = points[points.length - 1];
            const prev = points[points.length - 2];
            const diff = latest.v - prev.v;
            const pct = prev.v ? (diff / Math.abs(prev.v)) * 100 : null;
            const diffColor = diff >= 0 ? PROFIT_GREEN : LOSS_RED;
            diffHtml = `
              <div style="font-size:11px; font-weight:600; color:${diffColor}; margin-top:4px; padding-top:4px; border-top:1px solid ${GRID};">
                ${latest.yr} ${L('เทียบกับ', 'vs')} ${prev.yr}: ${diff >= 0 ? '+' : ''}${fmtBaht(diff)} ${L('บาท', 'THB')}${pct != null ? ` (${diff >= 0 ? '+' : ''}${pct.toFixed(1)}%)` : ''}
              </div>
            `;
          }
          return `
            <div style="padding:8px 12px; font-family:inherit; min-width:170px;">
              <div style="font-size:11px; color:${TEXT_SECONDARY}; margin-bottom:4px;">${monthLabels[dataPointIndex]}</div>
              ${rowsHtml}
              ${diffHtml}
            </div>
          `;
        },
      },
      // ป้ายตัวเลขลอยที่แต่ละจุด โชว์ "ผลต่างจากเดือนก่อนหน้า" (moDiff เดียวกับใน tooltip.custom
      // ด้านบน) พร้อมสีเขียว/แดงตามเครื่องหมาย — ใช้ annotations.points แทน dataLabels ปกติ เพราะ
      // dataLabels.style.colors รับได้แค่สีต่อ series (ต่อปี) ไม่ใช่ต่อจุด ให้สีแดงเฉพาะจุดที่ติดลบ
      // ไม่ได้ (เหตุผลเดียวกับที่ G-10 เลือก annotations.points แทน dataLabels ด้านบน) — ม.ค. ไม่มี
      // เดือนก่อนหน้าให้เทียบ ใช้ยอดสะสม ณ ม.ค. ตรงๆ (เท่ากับกำไรเดือนนั้นเป๊ะ), เดือนที่ยังไม่ถึง
      // (ปีปัจจุบันเกินเดือนนี้) ข้ามไปเลยไม่วางป้าย
      annotations: {
        points: [
          ...yearlyCumulativeProfit.flatMap(([yr, cumulative]) => {
            const curMonth = new Date().getMonth();
            return cumulative
              .map((rawV, mo) => {
                if (yr === currentYear && mo > curMonth) return null;
                const v = Math.round(rawV);
                const prevV = mo > 0 ? Math.round(cumulative[mo - 1]) : null;
                const diff = mo === 0 ? v : prevV != null ? v - prevV : null;
                if (diff == null) return null;
                return {
                  x: monthLabels[mo],
                  y: v,
                  marker: { size: 0 },
                  label: {
                    borderWidth: 0,
                    offsetY: -8,
                    style: {
                      color: diff >= 0 ? PROFIT_GREEN : LOSS_RED,
                      background: 'transparent',
                      fontSize: '8px',
                      fontWeight: 600,
                    },
                    text: `${diff >= 0 ? '+' : ''}${fmtBaht(diff)}`,
                  },
                };
              })
              .filter(Boolean);
          }),
          // ป้ายยอดสะสมรวม ณ จุดสุดท้ายของแต่ละเส้น วางไว้ใต้จุด (offsetY บวก, ตรงข้ามกับป้าย diff
          // รายเดือนด้านบนที่วางเหนือจุดเสมอ — จุดสุดท้ายมีป้ายทั้งสองแบบซ้อนกันคนละฝั่ง ไม่ทับกัน)
          // ให้เห็นยอดรวมล่าสุดของแต่ละปีได้ทันทีโดยไม่ต้อง hover, สีป้ายไล่ตามสีเส้นของปีนั้น
          ...yearlyCumulativeProfit.map(([yr, cumulative], i) => {
            const curMonth = new Date().getMonth();
            const lastMo = yr === currentYear ? Math.min(curMonth, 11) : 11;
            const v = Math.round(cumulative[lastMo]);
            return {
              x: monthLabels[lastMo],
              y: v,
              marker: { size: 0 },
              label: {
                borderWidth: 0,
                offsetY: 16,
                style: {
                  color: QUARTER_COLORS[i % QUARTER_COLORS.length],
                  background: 'transparent',
                  fontSize: '9px',
                  fontWeight: 700,
                },
                text: fmtBaht(v),
              },
            };
          }),
          // บรรทัดที่ 2 ใต้ป้ายยอดสะสมของ "ปีนี้" เท่านั้น (ปีอื่นจบไปแล้วไม่มี pace ให้เทียบ) — เทียบ
          // ยอดสะสม ณ เดือนล่าสุดของปีนี้ กับยอดสะสม ณ เดือนเดียวกันของปีที่แล้ว (apples-to-apples,
          // เดือนเทียบเดือน ไม่ใช่ทั้งปี) นำหน้า(เขียว)/ช้ากว่า(แดง) แล้วแต่เครื่องหมาย diff
          ...(() => {
            const curMonth = new Date().getMonth();
            const lastMo = Math.min(curMonth, 11);
            const curEntry = yearlyCumulativeProfit.find(([yr]) => yr === currentYear);
            const prevEntry = yearlyCumulativeProfit.find(([yr]) => yr === currentYear - 1);
            if (!curEntry || !prevEntry) return [];
            const curVal = Math.round(curEntry[1][lastMo]);
            const prevVal = Math.round(prevEntry[1][lastMo]);
            const diff = curVal - prevVal;
            const isAhead = diff >= 0;
            return [
              {
                x: monthLabels[lastMo],
                y: curVal,
                marker: { size: 0 },
                label: {
                  borderWidth: 0,
                  offsetY: 30,
                  style: {
                    color: isAhead ? PROFIT_GREEN : LOSS_RED,
                    background: 'transparent',
                    fontSize: '8px',
                    fontWeight: 600,
                  },
                  text: `${isAhead ? L('นำหน้า', 'Ahead') : L('ช้ากว่า', 'Behind')} ${fmtBaht(Math.abs(diff))}`,
                },
              },
            ];
          })(),
        ],
        // เส้นแนวนอนประ 2 เส้น: (1) ค่าสูงสุดของจุดปีนี้/ปีที่แล้ว ณ เดือนล่าสุด สีเขียว/แดงตามว่า
        // นำหน้า/ช้ากว่าปีที่แล้ว (logic เดียวกับป้าย "นำหน้า/ช้ากว่า" ด้านบน) (2) ค่าของปีนี้ตรงๆ ณ
        // เดือนปัจจุบัน สีตามเส้นของปีนี้เอง (เหมือนป้ายยอดสะสมท้ายเส้น) ให้แยกออกว่าเส้นไหนคือ "ค่า
        // จริงปีนี้" เส้นไหนคือ "เกณฑ์เปรียบเทียบ" (ถ้าปีนี้เป็นค่าสูงสุดอยู่แล้ว สองเส้นจะทับกันพอดี —
        // ตั้งใจ ไม่ได้ผิดพลาด)
        yaxis: (() => {
          const curMonth = new Date().getMonth();
          const lastMo = Math.min(curMonth, 11);
          const curEntry = yearlyCumulativeProfit.find(([yr]) => yr === currentYear);
          const prevEntry = yearlyCumulativeProfit.find(([yr]) => yr === currentYear - 1);
          if (!curEntry || !prevEntry) return [];
          const curVal = Math.round(curEntry[1][lastMo]);
          const prevVal = Math.round(prevEntry[1][lastMo]);
          const isAhead = curVal - prevVal >= 0;
          const maxVal = Math.max(curVal, prevVal);
          const maxLineColor = isAhead ? PROFIT_GREEN : LOSS_RED;
          const curYearIdx = yearlyCumulativeProfit.findIndex(([yr]) => yr === currentYear);
          const curYearColor = QUARTER_COLORS[curYearIdx % QUARTER_COLORS.length];
          return [
            {
              y: maxVal,
              borderColor: maxLineColor,
              strokeDashArray: 4,
              label: {
                borderColor: 'transparent',
                style: { color: maxLineColor, background: 'transparent', fontSize: '9px' },
                text: fmtBaht(maxVal),
              },
            },
            {
              y: curVal,
              borderColor: curYearColor,
              strokeDashArray: 4,
              label: {
                borderColor: 'transparent',
                offsetY: -10,
                style: { color: curYearColor, background: 'transparent', fontSize: '9px' },
                text: fmtBaht(curVal),
              },
            },
          ];
        })(),
      },
    };
  }, [yearlyCumulativeProfit, currentYear, L]);

  // G-11: double-click จุดในกราฟ (เดือนไหนก็ได้) → เปิด popover breakdown ว่ากำไรสะสมเดือนนั้นของ
  // แต่ละปีที่กำลังเทียบกันอยู่ มาจากโครงการไหนบ้าง — ใช้เทคนิคเดียวกับ handleG10DoubleClick (คำนวณ
  // เดือนจากตำแหน่ง x เทียบกับกรอบ .apexcharts-grid โดยตรง แทนพึ่ง apex dataPointSelection) เพราะ
  // ต้องการรวมข้อมูลของ "ทุกเส้น/ทุกปี" ที่เดือนนั้นพร้อมกันอยู่แล้ว ไม่ได้ผูกกับเส้นใดเส้นหนึ่ง — ต่าง
  // จาก G-10 ตรงที่นี่เป็นกราฟเส้น จุดข้อมูลเรียงจากขอบซ้ายถึงขอบขวาเป๊ะ (ไม่ใช่กึ่งกลางแท่งแบบ bar)
  // เลยหาร relX ด้วย (count-1) ไม่ใช่ count
  const g11ChartWrapRef = useRef(null);
  const [monthBreakdownPopover, setMonthBreakdownPopover] = useState(null);
  const handleG11DoubleClick = (event) => {
    const wrap = g11ChartWrapRef.current;
    if (!wrap || !yearlyCumulativeProfit.length) return;
    const gridEl = wrap.querySelector('.apexcharts-grid');
    if (!gridEl) return;
    const rect = gridEl.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || rect.width <= 0) return;
    const totalCategories = MONTH_LABELS_TH.length + 2; // 12 เดือน + 2 ช่องหลอกท้ายแกน (ดู xaxis ด้านบน)
    const relX = (event.clientX - rect.left) / rect.width;
    const idx = Math.min(totalCategories - 1, Math.max(0, Math.round(relX * (totalCategories - 1))));
    if (idx > 11) return; // ตกในช่องหลอก 2 ช่องท้าย ไม่มีข้อมูลจริงให้เปิด
    const monthLabels = L(MONTH_LABELS_TH, MONTH_LABELS_EN);
    const curMonthNow = new Date().getMonth();
    const groups = yearlyCumulativeProfit
      .map(([yr], i) => {
        // เดือนในอนาคตของปีปัจจุบัน ไม่มีจุดข้อมูลจริงบนเส้นเลย (apex เว้นเส้นไปแล้ว) เลยไม่ต้องโชว์
        // ปีนั้นในรายการเลย — ต่างจากเดือนที่ผ่านมาแล้วแต่บังเอิญไม่มีโครงการทำกำไรเดือนนั้นจริงๆ ที่
        // ยังโชว์ปีนั้นไว้ แค่ขึ้น "ไม่มีโครงการ / 0" แทน (เดิม filter ทิ้งไปเงียบๆ ทำให้ดูเหมือนปีนั้น
        // หายไปจาก popover ทั้งที่จริงๆ มีจุดอยู่บนกราฟ)
        if (yr === currentYear && idx > curMonthNow) return null;
        const jobs = yearMonthJobsMap.get(`${yr}-${idx}`) || [];
        return {
          year: yr,
          color: QUARTER_COLORS[i % QUARTER_COLORS.length],
          jobs,
          total: jobs.reduce((s, j) => s + j.value, 0),
        };
      })
      .filter(Boolean);
    if (!groups.length) return;
    setMonthBreakdownPopover({
      anchorPosition: { top: event.clientY, left: event.clientX },
      monthLabel: monthLabels[idx],
      groups,
    });
  };

  // สีต่อแท่ง: มีคดีฟ้องร้อง (สำคัญสุด) = เลือดหมู, ยังไม่เสร็จ100% = เขียว, ปกติ = ฟ้าเดิม
  const customerBarColors = useMemo(
    () =>
      byCustomer.map(([, , , , hasIncomplete, hasLawsuit]) =>
        hasLawsuit ? LAWSUIT_MAROON : hasIncomplete ? INCOMPLETE_GREEN : BLUE,
      ),
    [byCustomer],
  );

  // เผื่อพื้นที่ท้ายแท่ง/เหนือแท่งไว้ใส่ตัวเลข กัน label ไปทับแท่งตัวเองตอนค่าสูงสุดชนขอบกราฟพอดี
  // ต้อง derive จาก byCustomer (หลังเลือกค่าตามโหมดแล้ว) เพราะสเกลต่างกันระหว่างสองโหมด
  const customerAxisMax = useMemo(() => {
    const m = byCustomer.reduce((mx, [, v]) => Math.max(mx, v), 0);
    return m * 1.35 || undefined;
  }, [byCustomer]);

  const customerOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit',
      events: {
        // double-click แท่งไหนก็ได้ → เปิด popover breakdown รายโปรเจกต์ของแท่งนั้น (ทั้งแท่ง
        // คู่ค้าจริง/แท่งเดี่ยว/แท่ง "อื่นๆ" ใช้ handler เดียวกันหมด — jobs ผูกมากับ byCustomer
        // แต่ละ entry ไว้แล้วตั้งแต่ตอนคำนวณ)
        dataPointSelection: (event, chartContext, config) => {
          const idx = config?.dataPointIndex;
          if (idx == null || idx < 0) return;
          const now = Date.now();
          const last = lastBarClickRef.current;
          const barKey = `customer-${idx}`;
          if (last.id === barKey && now - last.time < 400) {
            lastBarClickRef.current = { id: null, time: 0 };
            const entry = byCustomer[idx];
            if (!entry) return;
            const isOthers = !!(othersEntry && entry[0] === othersEntry[0]);
            const jobs = entry[6] || [];
            setCustomerPopover({
              anchorPosition: { top: event.clientY, left: event.clientX },
              label: entry[0],
              // ใช้ผลรวมจริงจาก jobs เสมอ ไม่ใช่ entry[1] — เพราะโหมด "รับงานล่าสุด" (customerSort)
              // entry[1] คือต้นทุนงานล่าสุดงานเดียว ไม่ใช่ยอดรวมทั้งหมด จะทำให้ป้าย "รวม" ผิด
              total: jobs.reduce((s, j) => s + (j.value || 0), 0),
              jobs,
              isOthers,
            });
          } else {
            lastBarClickRef.current = { id: barKey, time: now };
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '55%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        dataLabels: { position: 'top' },
        distributed: true, // ให้สีต่อแท่งจาก colors[] ตามเงื่อนไข ไม่ใช่สีเดียวทั้ง series
      },
    },
    colors: customerBarColors,
    legend: { show: false }, // distributed:true ปกติจะขึ้น legend ทีละชื่อคู่ค้า ไม่ต้องการแบบนั้น
    dataLabels: {
      enabled: true,
      // เว้นช่องไฟ ~10px ระหว่างยอดเงินกับ (เดือน/ปี) ล่าสุด — ทำได้แค่ประมาณด้วย
      // non-breaking space เพราะ apex render เป็น <text> เดียว ไม่รองรับ margin จริง
      formatter: (v, opts) => {
        const latest = byCustomer[opts.dataPointIndex]?.[2];
        return latest ? `${fmtBaht(v)}   (${latest})` : fmtBaht(v);
      },
      textAnchor: 'start', // ให้ตัวเลขวิ่งออกจากปลายแท่งไปทางขวา ไม่ใช่ชนขอบแล้วยาวย้อนทับแท่ง
      offsetX: 40,
      style: { colors: [TEXT_SECONDARY], fontSize: '10px', fontWeight: 600 },
    },
    grid: { borderColor: GRID, strokeDashArray: 0 },
    xaxis: {
      categories: byCustomer.map(([name]) => name),
      max: customerAxisMax,
      labels: {
        style: { colors: AXIS_INK, fontSize: '11px' },
        formatter: (v) => fmtBaht(v),
      },
    },
    yaxis: { labels: { style: { colors: TEXT_SECONDARY, fontSize: '11px' } } },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const name = w.globals.labels[dataPointIndex];
        const val = series[seriesIndex][dataPointIndex];
        const latest = byCustomer[dataPointIndex]?.[2];
        return `
          <div style="padding:8px 12px; font-family:inherit;">
            <div style="font-size:11px; color:${TEXT_SECONDARY}; margin-bottom:2px;">${name}</div>
            <div style="font-size:12px; font-weight:600; color:#0b0b0b;">${fmtBaht(val)} ${L('บาท', 'THB')}</div>
            ${latest ? `<div style="font-size:11px; color:${AXIS_INK}; margin-top:4px;">Latest PO&nbsp;&nbsp;&nbsp;<span style="color:${FADED_TEXT};">(${latest})</span></div>` : ''}
          </div>
        `;
      },
    },
  };

  // แยกยอดต่อปีเป็น 4 series ตามไตรมาส ให้ apex stack ซ้อนกันเป็นแท่งเดียวต่อปี แต่ละช่วงคนละสี —
  // ใส่ group: 'po' ให้ apex รู้ว่า 4 series นี้ stack รวมกันเป็นแท่งเดียว (คนละกลุ่มกับแท่งกำไร)
  const quarterSeries = [0, 1, 2, 3].map((qi) => ({
    name: `Q${qi + 1}`,
    group: 'po',
    data: byYear.map(([, , , quarters]) => Math.round(quarters?.[qi] || 0)),
  }));

  // แท่งกำไรคู่กับแท่ง PO ต่อปี (group: 'profit' แยกจาก group: 'po' — apex เลยวางแท่งนี้ติดกันข้างๆ
  // แทนที่จะ stack ซ้อนรวมเป็นแท่งเดียวกัน) ยอดกำไรต่อปีดึงจาก yearlyCumulativeProfit ตัวเดียวกับที่
  // G-11 ใช้ (ค่าสะสม ณ เดือนสุดท้ายที่มีข้อมูลจริงของปีนั้น = กำไรรวมทั้งปี หรือ YTD สำหรับปีปัจจุบัน
  // ที่ยังไม่จบ — ตรรกะเดียวกับที่แท่ง PO เป็น YTD โดยธรรมชาติอยู่แล้วเช่นกัน) ไม่ต้องคำนวณซ้ำ
  const yearlyProfitTotals = byYear.map(([yr]) => {
    const entry = yearlyCumulativeProfit.find(([y]) => y === yr);
    if (!entry) return 0;
    const cumulative = entry[1];
    for (let i = cumulative.length - 1; i >= 0; i -= 1) {
      if (cumulative[i] != null) return cumulative[i];
    }
    return 0;
  });
  const profitSeries = {
    name: L('กำไร', 'Profit'),
    group: 'profit',
    data: yearlyProfitTotals.map((v) => Math.round(v)),
  };
  const idSeries = {
    name: 'ID PAID',
    group: 'id',
    data: byYear.map(([yr]) => Math.round(idByYear.get(yr)?.sum || 0)),
  };
  const netProfitValues = byYear.map(([yr], idx) =>
    Math.round((yearlyProfitTotals[idx] || 0) - (idByYear.get(yr)?.sum || 0)),
  );
  let accumulatedNetProfit = 0;
  const accumulatedNetProfitValues = netProfitValues.map((value) => {
    accumulatedNetProfit += value;
    return accumulatedNetProfit;
  });
  const getNetProfitColor = (value) => (value >= 0 ? NET_PROFIT_POSITIVE : NET_PROFIT_NEGATIVE);
  const idProfitSeries = {
    name: 'Net Profit',
    group: 'id-profit',
    data: netProfitValues,
  };
  const accumulatedTrendPoints = [0, ...accumulatedNetProfitValues];
  const accumulatedTrendColors = accumulatedTrendPoints.slice(1).map((value, idx) =>
    value >= accumulatedTrendPoints[idx] ? NET_PROFIT_POSITIVE : NET_PROFIT_NEGATIVE,
  );
  const accumulatedAxisStep = 1000000;
  const accumulatedAxisMin =
    Math.min(...accumulatedTrendPoints) < 0
      ? Math.floor(Math.min(...accumulatedTrendPoints) / accumulatedAxisStep) * accumulatedAxisStep
      : 0;
  const accumulatedAxisMax = Math.max(
    accumulatedAxisStep,
    Math.ceil(Math.max(...accumulatedTrendPoints) / accumulatedAxisStep) * accumulatedAxisStep,
  );
  const accumulatedTickIntervals = (accumulatedAxisMax - accumulatedAxisMin) / accumulatedAxisStep;
  // แยกเป็น series ละหนึ่งช่วง เพื่อให้ช่วงขาขึ้นเป็นเขียวและช่วงขาลงเป็นแดง
  const accumulatedNetProfitSeries = accumulatedTrendColors.map((color, segmentIndex) => ({
    name: color === NET_PROFIT_POSITIVE ? L('เพิ่มขึ้น', 'Increase') : L('ลดลง', 'Decrease'),
    data: accumulatedTrendPoints.map((value, pointIndex) => ({
      x: pointIndex,
      y: pointIndex === segmentIndex || pointIndex === segmentIndex + 1 ? value : null,
    })),
  }));
  const accumulatedXAxisLabels = [L('จุดเริ่มต้น', 'Origin'), ...byYear.map(([year]) => String(year))];
  const accumulatedDeltaAnnotations = accumulatedTrendPoints.slice(1).flatMap((value, segmentIndex) => {
    if (segmentIndex === 0) return [];
    const previousValue = accumulatedTrendPoints[segmentIndex];
    const delta = value - previousValue;
    const color = delta >= 0 ? NET_PROFIT_POSITIVE : NET_PROFIT_NEGATIVE;
    return [
      {
        x: segmentIndex + 0.5,
        y: (previousValue + value) / 2,
        marker: { size: 0 },
        label: {
          borderColor: 'transparent',
          offsetY: -5,
          text: `Δ ${delta >= 0 ? '+' : ''}${fmtBaht(delta)}`,
          style: { background: 'transparent', color, fontSize: '10px', fontWeight: 700 },
        },
      },
    ];
  });
  const minNetProfit = Math.min(0, ...netProfitValues, ...accumulatedNetProfitValues);
  // แกน G-4 เพิ่มทีละ 4 ล้านบาท โดยให้ 0 เป็น tick จริงเสมอ
  const yearTickStep = 4000000;
  const yearAxisMin = minNetProfit < 0 ? Math.floor(minNetProfit / yearTickStep) * yearTickStep : 0;
  const yearAxisDisplayMax = Math.max(
    yearTickStep,
    Math.ceil(Math.max(yearAxisMax || 0, ...accumulatedNetProfitValues) / yearTickStep) * yearTickStep,
  );
  const yearTickIntervals = (yearAxisDisplayMax - yearAxisMin) / yearTickStep;
  const currentYearLabelPosition = byYear.findIndex(([year]) => year === new Date().getFullYear()) + 1;
  // ปีอดีตมี PO มากกว่าปัจจุบัน = แดง, น้อยกว่าหรือเท่ากับปัจจุบัน = เขียว
  const yoyComparisonColor = yoyDiff < 0 ? YOY_RED : NET_PROFIT_POSITIVE;
  const poTotalLabelSVG = (total, count) => ({
    SVG: `<g><text class="g4-po-value" x="0" y="10" text-anchor="middle" fill="${TEXT_SECONDARY}" font-size="10" font-weight="600">${fmtBaht(total)} (${count} ${L('งาน', 'jobs')})</text></g>`,
    offsetX: -30,
    offsetY: -14,
  });
  const netProfitLabelSVG = (value) => ({
    SVG: `<g><text class="g4-net-profit-value" text-anchor="middle" fill="${getNetProfitColor(value)}" font-size="10" font-weight="700"><tspan x="0" dy="0">Net Profit</tspan><tspan x="0" dy="12">${fmtBaht(value)}</tspan></text></g>`,
    offsetX: 52,
    offsetY: value < 0 ? 18 : -24,
  });
  const handleG4BarSelection = (event, config) => {
    const dataPointIndex = config?.dataPointIndex;
    const seriesIndex = config?.seriesIndex;
    if (dataPointIndex == null || dataPointIndex < 0 || seriesIndex == null || seriesIndex > 6) return;

    const now = Date.now();
    const barKey = `g4-${seriesIndex}-${dataPointIndex}`;
    const last = lastBarClickRef.current;
    if (last.id !== barKey || now - last.time >= 400) {
      lastBarClickRef.current = { id: barKey, time: now };
      return;
    }
    lastBarClickRef.current = { id: null, time: 0 };

    const [year, , , , yearJobs = []] = byYear[dataPointIndex] || [];
    if (!year) return;
    const idJobs = idByYear.get(year)?.jobs || [];
    let label;
    let jobs;

    if (seriesIndex <= 3) {
      label = `${year} · Q${seriesIndex + 1} PO`;
      jobs = yearJobs.filter((job) => job.quarter === seriesIndex);
    } else if (seriesIndex === 4) {
      label = `${year} · Profit`;
      jobs = yearJobs.map((job) => ({ ...job, value: Number(job.profit) || 0 }));
    } else if (seriesIndex === 5) {
      label = `${year} · ID PAID`;
      jobs = idJobs;
    } else {
      label = `${year} · Net Profit`;
      jobs = [
        ...yearJobs.map((job) => ({ ...job, value: Number(job.profit) || 0 })),
        ...idJobs.map((job) => ({ ...job, value: -(Number(job.value) || 0) })),
      ];
    }

    setCustomerPopover({
      anchorPosition: { top: event?.clientY || 0, left: event?.clientX || 0 },
      label,
      total: jobs.reduce((sum, job) => sum + (Number(job.value) || 0), 0),
      jobs,
      isOthers: true,
      defaultSort: { key: 'value', dir: 'desc' },
    });
  };
  const yearOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      fontFamily: 'inherit',
      events: {
        dataPointSelection: (event, chartContext, config) => handleG4BarSelection(event, config),
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '45%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last', // โค้งมุมแค่ไตรมาสบนสุดของแต่ละแท่ง ไม่ใช่ทุกช่วง
      },
    },
    colors: [
      ...QUARTER_COLORS,
      G4_PROFIT_GREEN,
      ID_RED,
      ({ value }) => getNetProfitColor(Number(value) || 0),
    ],
    // legend ของ apex ปิดไว้ — ใช้ legend เองด้านนอก (JSX) แทน เพราะ legend ของ apex กิน
    // พื้นที่จากใน Chart เอง (flex:1) โดยตรง ทำให้พื้นที่แท่งกราฟจริงหดเล็กลงทุกครั้งที่มี
    // แถวข้อความเพิ่ม — ย้ายออกไปเป็น header คงที่แทนเหมือนกราฟ "PO by customer"
    legend: { show: false },
    dataLabels: { enabled: false },
    grid: { borderColor: GRID, strokeDashArray: 0 },
    xaxis: {
      categories: byYear.map(([y]) => String(y)),
      labels: { style: { colors: AXIS_INK, fontSize: '11px' } },
    },
    yaxis: {
      min: yearAxisMin,
      max: yearAxisDisplayMax,
      tickAmount: yearTickIntervals,
      labels: {
        style: { colors: TEXT_SECONDARY, fontSize: '11px' },
        formatter: (v) => fmtBaht(v),
      },
    },
    tooltip: {
      shared: true, // โชว์ทุกไตรมาสของปีนั้นพร้อมกันตอน hover แท่งเดียว
      intersect: false, // apex บังคับต้องปิดคู่กับ shared:true เสมอ ไม่งั้น throw error ทันที
      custom: ({ dataPointIndex }) => {
        const [year, total, count, quarters] = byYear[dataPointIndex] || [];
        const avoidDirection = dataPointIndex < byYear.length / 2 ? 'right' : 'left';
        const rows = quarterSeries
          .map((s, qi) => {
            const v = quarters?.[qi] || 0;
            if (!v) return '';
            return `<div style="display:flex; justify-content:space-between; gap:12px; font-size:11px; color:${TEXT_SECONDARY};">
              <span><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${QUARTER_COLORS[qi]};margin-right:4px;"></span>${s.name}</span>
              <span>${fmtBaht(v)}</span>
            </div>`;
          })
          .join('');
        const profitVal = yearlyProfitTotals[dataPointIndex] || 0;
        const idInfo = idByYear.get(year) || { sum: 0, count: 0 };
        const profitAfterId = profitVal - idInfo.sum;
        return `
          <div class="g4-tooltip--${avoidDirection}" style="padding:8px 12px; font-family:inherit; min-width:160px;">
            <div style="font-size:11px; color:${TEXT_SECONDARY}; margin-bottom:4px;">${year}</div>
            ${rows}
            <div style="font-size:12px; font-weight:600; color:#0b0b0b; margin-top:4px; border-top:1px solid ${GRID}; padding-top:4px;">${fmtBaht(total)} ${L('บาท', 'THB')} (${count} ${L('งาน', 'jobs')})</div>
            <div style="display:flex; justify-content:space-between; gap:12px; font-size:11px; color:${G4_PROFIT_GREEN}; margin-top:2px;">
              <span><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${G4_PROFIT_GREEN};margin-right:4px;"></span>${L('กำไร', 'Profit')}</span>
              <span style="font-weight:600;">${fmtBaht(profitVal)}</span>
            </div>
            <div style="display:flex; justify-content:space-between; gap:12px; font-size:11px; color:${ID_RED}; margin-top:2px;">
              <span><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${ID_RED};margin-right:4px;"></span>ID PAID (JSID)</span>
              <span style="font-weight:600;">${fmtBaht(idInfo.sum)}</span>
            </div>
            <div style="display:flex; justify-content:space-between; gap:12px; font-size:11px; color:${getNetProfitColor(profitAfterId)}; margin-top:2px;">
              <span><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${getNetProfitColor(profitAfterId)};margin-right:4px;"></span>Net Profit</span>
              <span style="font-weight:600;">${fmtBaht(profitAfterId)}</span>
            </div>
          </div>
        `;
      },
    },
    annotations: {
      // การ์ด G-4 แคบ จึงแสดงเฉพาะยอด PO รวมเหนือแต่ละปี เพื่อไม่ให้ป้ายกำไร/JSID/Profit-ID
      // ซ้อนทับกัน ข้อมูลทั้งหมดยังอยู่ใน tooltip เมื่อ hover แท่งกราฟ
      points: [
        ...byYear.map(([year, total, count]) => ({
          x: String(year),
          y: total,
          marker: { size: 0 },
          customSVG: poTotalLabelSVG(total, count),
        })),
        ...byYear.map(([year], idx) => {
          const netProfit = netProfitValues[idx] || 0;
          return {
            x: String(year),
            y: netProfit,
            marker: { size: 0 },
            customSVG: netProfitLabelSVG(netProfit),
          };
        }),
      ],
      yaxis: [
        // เส้นฐานของแท่งบวก/ลบต้องอยู่ที่ 0 เสมอ
        { y: 0, borderColor: AXIS_INK, strokeDashArray: 0, borderWidth: 1 },
        // ชื่อค่าอ้างอิงปีก่อนวางเหนือค่าที่ปลายเส้น โดยใช้ annotation โปร่งใสเพื่อไม่วาดเส้นซ้ำ
        {
          y: lastYearToDatePo,
          borderColor: 'transparent',
          borderWidth: 0,
          label: {
            borderColor: 'transparent',
            position: 'right',
            textAnchor: 'middle',
            offsetX: -4,
            offsetY: -19,
            text: L('PO ปีก่อน YTD', 'Last-year YTD PO'),
            style: {
              background: 'transparent',
              color: yoyComparisonColor,
              fontSize: '10px',
              fontWeight: 700,
            },
          },
        },
        // PO ของปีก่อนสะสมถึงวันเดียวกัน ใช้เส้นประและเปลี่ยนสีตามผลเทียบกับปีปัจจุบัน
        {
          y: lastYearToDatePo,
          borderColor: yoyComparisonColor,
          strokeDashArray: 5,
          borderWidth: 2,
          label: {
            borderColor: 'transparent',
            position: 'right',
            textAnchor: 'middle',
            offsetX: -4,
            offsetY: -7,
            text: fmtBaht(lastYearToDatePo),
            style: {
              background: 'transparent',
              color: yoyComparisonColor,
              fontSize: '10px',
              fontWeight: 700,
            },
          },
        },
      ],
    },
  };
  // G-4 แท็บรายเดือน: แท่งเรียงกลุ่ม (ไม่ stack) PO / กำไร / ID PAID / Net Profit ต่อเดือน
  const monthSeries = [
    { name: 'PO', data: byMonth.map(([, v]) => Math.round(v.po)) },
    { name: L('กำไร', 'Profit'), data: byMonth.map(([, v]) => Math.round(v.profit)) },
    { name: 'ID PAID', data: byMonth.map(([, v]) => Math.round(v.id)) },
    { name: 'Net Profit', data: byMonth.map(([, v]) => Math.round(v.profit - v.id)) },
  ];
  // ดับเบิลคลิกแท่งรายเดือน → popover รายการโครงการ เหมือนแท็บรายปี (handleG4BarSelection)
  const handleMonthBarSelection = (event, config) => {
    const dataPointIndex = config?.dataPointIndex;
    const seriesIndex = config?.seriesIndex;
    if (dataPointIndex == null || dataPointIndex < 0 || seriesIndex == null) return;

    // คลิกเดียว (รอ 300ms) = เปิด popover รายการโครงการ — ถ้าเป็นดับเบิลคลิก (ซูมแกน Y ของเดือน,
    // ดู MonthScrollChart) จะยกเลิก timer นี้ทิ้งก่อน popover ไม่เด้งซ้อน
    clearTimeout(g4MonthClickTimerRef.current);
    const point = { clientX: event?.clientX || 0, clientY: event?.clientY || 0 };
    g4MonthClickTimerRef.current = setTimeout(
      () => openMonthPopover(point, seriesIndex, dataPointIndex),
      300,
    );
  };
  const openMonthPopover = (event, seriesIndex, dataPointIndex) => {
    const [key, month] = byMonth[dataPointIndex] || [];
    if (!key) return;
    const [y, m] = key.split('-');
    const monthLabel = `${m}/${y.slice(2)}`;
    let label;
    let jobs;
    if (seriesIndex === 0) {
      label = `${monthLabel} · PO`;
      jobs = month.jobs;
    } else if (seriesIndex === 1) {
      label = `${monthLabel} · Profit`;
      jobs = month.jobs.map((job) => ({ ...job, value: Number(job.profit) || 0 }));
    } else if (seriesIndex === 2) {
      label = `${monthLabel} · ID PAID`;
      jobs = month.idJobs;
    } else {
      label = `${monthLabel} · Net Profit`;
      jobs = [
        ...month.jobs.map((job) => ({ ...job, value: Number(job.profit) || 0 })),
        ...month.idJobs.map((job) => ({ ...job, value: -(Number(job.value) || 0) })),
      ];
    }
    setCustomerPopover({
      anchorPosition: { top: event?.clientY || 0, left: event?.clientX || 0 },
      label,
      total: jobs.reduce((sum, job) => sum + (Number(job.value) || 0), 0),
      jobs,
      isOthers: true,
      defaultSort: { key: 'value', dir: 'desc' },
    });
  };
  const monthOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit',
      zoom: { enabled: false },
      events: { dataPointSelection: (event, chartContext, config) => handleMonthBarSelection(event, config) },
    },
    plotOptions: { bar: { columnWidth: '75%', borderRadius: 2, borderRadiusApplication: 'end' } },
    colors: [QUARTER_COLORS[0], G4_PROFIT_GREEN, ID_RED, NET_PROFIT_POSITIVE],
    legend: { show: true, position: 'top', fontSize: '11px', labels: { colors: TEXT_SECONDARY } },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 1, colors: ['transparent'] },
    grid: { borderColor: GRID, strokeDashArray: 0 },
    xaxis: {
      categories: byMonth.map(([key]) => {
        const [y, m] = key.split('-');
        return `${m}/${y.slice(2)}`;
      }),
      labels: { rotate: -45, style: { colors: AXIS_INK, fontSize: '10px' } },
    },
    yaxis: { labels: { style: { colors: TEXT_SECONDARY, fontSize: '11px' }, formatter: (v) => fmtBaht(v) } },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (v) => `${fmtBaht(v)} ${L('บาท', 'THB')}` },
    },
  };

  const accumulatedYearOptions = {
    chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: true } },
    colors: accumulatedTrendColors,
    stroke: { width: 3, curve: 'smooth' },
    markers: { size: 4, strokeWidth: 0 },
    dataLabels: {
      enabled: true,
      formatter: (value, { seriesIndex, dataPointIndex }) => {
        if (seriesIndex === 0 && dataPointIndex === 0) return '0';
        return dataPointIndex === seriesIndex + 1 ? fmtBaht(value) : '';
      },
      offsetY: -7,
      style: { colors: accumulatedTrendColors, fontSize: '10px', fontWeight: 700 },
      background: { enabled: false },
    },
    legend: { show: false },
    grid: { borderColor: GRID, strokeDashArray: 0 },
    xaxis: {
      type: 'numeric',
      min: 0,
      max: accumulatedXAxisLabels.length - 1,
      tickAmount: Math.max(accumulatedXAxisLabels.length - 1, 1),
      labels: {
        style: { colors: AXIS_INK, fontSize: '11px' },
        formatter: (value) => {
          const index = Math.round(value);
          return Math.abs(value - index) < 0.01 ? accumulatedXAxisLabels[index] || '' : '';
        },
      },
    },
    yaxis: {
      min: accumulatedAxisMin,
      max: accumulatedAxisMax,
      tickAmount: accumulatedTickIntervals,
      labels: {
        style: { colors: TEXT_SECONDARY, fontSize: '11px' },
        formatter: (value) => fmtBaht(value),
      },
    },
    annotations: {
      yaxis: [{ y: 0, borderColor: AXIS_INK, strokeDashArray: 0, borderWidth: 1 }],
      points: accumulatedDeltaAnnotations,
    },
    tooltip: {
      shared: false,
      intersect: false,
      custom: ({ dataPointIndex }) => {
        const label =
          dataPointIndex === 0
            ? L('จุดเริ่มต้น', 'Origin')
            : String(byYear[dataPointIndex - 1]?.[0] || '');
        return `
          <div style="padding:8px 12px;font-family:inherit;">
            <div style="font-size:11px;color:${TEXT_SECONDARY};">${label}</div>
            <div style="font-size:12px;font-weight:700;">${fmtBaht(accumulatedTrendPoints[dataPointIndex])}</div>
          </div>`;
      },
    },
  };
  const g4TooltipAvoidSx = {
    [`& .g4-bar-chart .apexcharts-xaxis-texts-g text:nth-of-type(${currentYearLabelPosition})`]: {
      fill: '#111 !important',
      fontWeight: '700 !important',
      paintOrder: 'stroke',
      stroke: '#fde047',
      strokeWidth: '7px',
      strokeLinejoin: 'round',
    },
    '& .g4-bar-chart .apexcharts-tooltip:has(.g4-tooltip--right)': {
      transform: 'translateX(50%) !important',
      transition: 'transform 120ms ease-out !important',
    },
    '& .g4-bar-chart .apexcharts-tooltip:has(.g4-tooltip--left)': {
      transform: 'translateX(-50%) !important',
      transition: 'transform 120ms ease-out !important',
    },
  };

  // ลดพื้นที่ต่อแท่งลง 20% (34 → 27) ให้ช่องไฟระหว่างแท่งแคบลง ตามที่ขอ (แท่งเบียดกันมากขึ้น
  // ไม่ใช่แค่กราฟรวมเตี้ยลง — ทั้งความหนาแท่งและช่องว่างหดตามสัดส่วนเดียวกัน เพราะ barHeight %
  // ใน customerOptions ยังคงเดิม แค่พื้นที่ต่อแท่ง (band) เล็กลง)
  const customerChartHeight = Math.max(byCustomer.length * 27 + 60, 200);

  // ตัวช่วยสร้าง options ให้ 3 การ์ด top-10 (กำไรสูงสุด/เกิน budget มากสุด/ขาดทุนสูงสุด) — โครงสร้าง
  // เหมือนกันทุกอย่าง ต่างแค่สี ใช้ label ย่อ "JS_xxxxx" บนแกน x ส่วนชื่อคู่ค้า+ชื่อโครงการเต็มไป
  // อยู่ใน tooltip ตอน hover เท่านั้น (ชื่อคู่ค้าก่อน ตามด้วยชื่อโครงการ ตามที่ขอ) — ปิด dataLabels
  // ไปเลย ไม่ลอง offsetY/position แบบกราฟอื่น เพราะค่าที่นี่มีทั้งบวก/ลบปนกันได้ (กำไร/ขาดทุน)
  // ทำให้ตำแหน่งป้ายที่ใช้ได้กับแท่งบวกจะกลับไปทับแท่งลบเอง เคยเจอปัญหานี้มาแล้วรอบก่อนๆ ในแท็บนี้
  const buildTopListOptions = (entries, color, highlightLawsuit) => {
    // แท่งของ "ปีนี้" (ตามวันที่รับงาน, index 5 = timestamp) ใช้สีเดิมของการ์ด ส่วนปีอื่นๆ
    // เป็นเทา (AXIS_INK) — เน้นให้เห็นว่างานไหนเป็นของปีปัจจุบันเทียบกับปีก่อนๆ ในลิสต์เดียวกัน
    // ยกเว้นการ์ด "ขาดทุนสูงสุด" (highlightLawsuit=true): โครงการที่มีคดีฟ้องร้อง (index 6) ทาสี
    // เลือดหมูแทนเสมอ ไม่ว่าจะปีไหนก็ตาม — ทับ logic ปี/เทาด้านบน เพราะสำคัญกว่า
    const currentYear = new Date().getFullYear();
    const barColors = entries.map(([, , , , , ts, hasLawsuit]) =>
      highlightLawsuit && hasLawsuit
        ? LAWSUIT_MAROON
        : ts && new Date(ts).getFullYear() === currentYear
        ? color
        : AXIS_INK,
    );
    return {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'inherit',
        events: {
          // double-click แท่งไหนก็ได้ใน 3 การ์ด top-10 นี้ → เปิด popup โปรเจกต์นั้น เข้าแท็บ
          // "แดชบอร์ด" (T-1) เลย (sheet 0, ค่า default ของ handleOpenProject อยู่แล้ว) — ดู
          // คอมเมนต์ที่ lastBarClickRef ด้านบนว่าทำไมไม่ใช้ dblClick ตรงๆ
          dataPointSelection: (event, chartContext, config) => {
            const idx = config?.dataPointIndex;
            const projectId = idx != null && idx >= 0 ? entries[idx]?.[7] : null;
            if (!projectId) return;
            const now = Date.now();
            const last = lastBarClickRef.current;
            if (last.id === projectId && now - last.time < 400) {
              lastBarClickRef.current = { id: null, time: 0 };
              if (onOpenProject) onOpenProject(projectId);
            } else {
              lastBarClickRef.current = { id: projectId, time: now };
            }
          },
        },
      },
      plotOptions: {
        bar: { columnWidth: '50%', borderRadius: 4, borderRadiusApplication: 'end', distributed: true },
      },
      colors: barColors,
      legend: { show: false }, // distributed:true ปกติขึ้น legend ทีละแท่ง ไม่ต้องการแบบนั้น
      dataLabels: { enabled: false },
      grid: { borderColor: GRID, strokeDashArray: 0 },
      xaxis: {
        // แสดง "(MM/YY)" ของวันที่รับงานเป็นบรรทัดที่ 2 ใต้ชื่อ Job — apex รองรับ label หลายบรรทัด
        // ต่อ category ด้วย array of strings
        categories: entries.map(([label, , , , dateLabel]) => (dateLabel ? [label, `(${dateLabel})`] : [label])),
        labels: { style: { colors: AXIS_INK, fontSize: '10px' } },
      },
      yaxis: {
        labels: { style: { colors: TEXT_SECONDARY, fontSize: '10px' }, formatter: (v) => fmtBaht(v) },
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex }) => {
          const [, , cust, name, dateLabel] = entries[dataPointIndex] || [];
          const val = series[seriesIndex][dataPointIndex];
          return `
            <div style="padding:8px 12px; font-family:inherit; max-width:220px;">
              <div style="font-size:11px; font-weight:600; color:#0b0b0b;">${cust}</div>
              <div style="font-size:11px; color:${TEXT_SECONDARY}; margin-bottom:4px;">${name}</div>
              <div style="font-size:12px; font-weight:600; color:${barColors[dataPointIndex] || color};">${fmtBaht(val)} ${L('บาท', 'THB')}</div>
              ${dateLabel ? `<div style="font-size:11px; color:${AXIS_INK}; margin-top:4px;">${L('รับงาน', 'Job date')}&nbsp;&nbsp;&nbsp;<span style="color:${FADED_TEXT};">(${dateLabel})</span></div>` : ''}
            </div>
          `;
        },
      },
    };
  };

  const topProfitOptions = buildTopListOptions(topProfit, PROFIT_GREEN);
  const topOverBudgetOptions = buildTopListOptions(topOverBudget, OVER_BUDGET_ORANGE);
  const topLossOptions = buildTopListOptions(topLoss, LOSS_RED, true);

  // สรุปจำนวนโปรเจกต์รวม + มูลค่ารวมของทุกคู่ค้าที่กำลังแสดงอยู่ในกราฟ G-3 (ไม่ผูกกับโหมด
  // customerSort — นับจาก jobs จริงของแต่ละแท่งเสมอ ไม่ใช่ค่าที่โชว์บนแท่งซึ่งโหมด "รับงานล่าสุด"
  // จะเป็นแค่ต้นทุนงานล่าสุดงานเดียว ไม่ใช่ยอดรวม)
  const customerTotals = byCustomer.reduce(
    (acc, e) => {
      const jobs = e[6] || [];
      acc.count += jobs.length;
      acc.value += jobs.reduce((s, j) => s + (j.value || 0), 0);
      return acc;
    },
    { count: 0, value: 0 },
  );

  // สรุปจำนวนโปรเจกต์รวม + มูลค่าการฟ้องร้องรวมของทุกคู่ค้าในกราฟ G-12 — jobCount (index 3) นับ
  // เป็นจำนวนโปรเจกต์อยู่แล้ว (1 job = 1 โปรเจกต์ที่ถูกฟ้องร้อง), sum (index 1) คือยอดรวมต่อคู่ค้า
  const lawsuitTotals = lawsuitCustomers.reduce(
    (acc, e) => {
      acc.count += e[3];
      acc.value += e[1];
      return acc;
    },
    { count: 0, value: 0 },
  );

  // G-6: ช่วงปีก่อนหน้าที่นับ (YEAR_TREND_MIN..retentionYear-1) — ถ้าเหลือแค่ปีเดียว (เช่นตอนนี้
  // 2025 เท่านั้น) โชว์แค่ปีเดียวเฉยๆ ไม่ใช่ช่วงซ้ำตัวเองแบบ "(2025–2025)" ที่ดูเหมือนพิมพ์ผิด —
  // พอมีมากกว่า 1 ปี (เช่นปี 2027 ที่ retentionYear-1=2026) ค่อยโชว์เป็นช่วงจริงๆ
  const priorYearsLabel =
    YEAR_TREND_MIN === retentionYear - 1 ? `${YEAR_TREND_MIN}` : `${YEAR_TREND_MIN}–${retentionYear - 1}`;

  // series ของ G-3/G-10/G-11 แยกเป็นตัวแปรกลาง ให้การ์ดปกติกับ Dialog เต็มจอ (ปุ่ม OpenInFullIcon) ใช้
  // ชุดข้อมูลเดียวกันเป๊ะ ไม่ต้องก็อปลอจิกซ้ำ (โดยเฉพาะ G-11 ที่มีเงื่อนไข null เดือนอนาคต/เติมช่อง
  // หลอกท้ายแกน — ถ้าก็อปแยกกันเสี่ยงแก้จุดเดียวแล้วอีกจุดตกหล่น) G-4 ใช้ quarterSeries ตรงๆ อยู่แล้ว
  // เลยไม่ต้องมีตัวแปรแยก
  const g3Series = [{ name: L('ยอด PO', 'PO amount'), data: byCustomer.map(([, v]) => Math.round(v)) }];
  const g10Series = [
    {
      name: companyMetric === 'bgPct' ? L('%กำไร/BG', '%Profit/BG') : L('กำไร/ขาดทุน', 'Profit/Loss'),
      data: companyProfitTop.map((e) => Math.round(companyMetric === 'bgPct' ? e.bgPct : e.profit)),
    },
  ];
  const g11Series = yearlyCumulativeProfit.map(([yr, cumulative]) => ({
    name: String(yr),
    data: [
      ...cumulative.map((v, mo) => (yr === currentYear && mo > new Date().getMonth() ? null : Math.round(v))),
      null,
      null,
    ],
  }));

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <StatBox
          devNum={1}
          devName="Project Count"
          label={
            <>
              {L('จำนวนโครงการ', 'Project Count')}{' '}
              <Box component="span" sx={{ fontSize: '0.62rem', color: AXIS_INK }}>
                ({L('ขอบเขตข้อมูล: เฉพาะโครงการ JS_ และปี 2025 เป็นต้นไป', 'Scope: JS_ projects only & 2025 onward')})
              </Box>
            </>
          }
          value={
            <>
              {projectCount.toLocaleString('th-TH')}{' '}
              <Box component="span" sx={{ fontSize: '0.9rem', fontWeight: 400, color: TEXT_SECONDARY }}>
                {L('โครงการ', 'Projects')}
              </Box>
            </>
          }
        />
        <StatBox
          devNum={2}
          devName="Total PO"
          label={L('ยอด PO รวม (2025 เป็นต้นไป)', 'Total PO (2025 onward)')}
          value={`${fmtBaht(totalPo)} ${L('บาท', 'THB')}`}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          // G-4 แคบเท่า G-12 (1 ใน 3 ของแถว G-5/G-6/G-12 ด้านล่าง ที่มีความกว้าง container เท่ากัน) —
          // G-3 ขยายกินพื้นที่ส่วนที่เหลือ (2fr) แทน
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Card
          sx={{
            position: 'relative',
            minWidth: 0, // กัน CSS grid ขยายคอลัมน์ตามความกว้างจริงของ dataLabel ในกราฟ (ทำให้เกิด
            // scroll แนวนอนทั้งหน้า) — grid item ปกติมี min-width:auto ไม่ยอมหดตาม track เอง
            p: 2,
            border: '1px solid #e1e0d9',
            boxShadow: 'none',
            height: TOP_TRIO_CARD_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DevCardBadge num={3} name="PO by customer" />
          <IconButton
            size="small"
            onClick={() => setExpandedChart('g3')}
            sx={{ position: 'absolute', top: 4, right: 42, zIndex: 5, p: 0.5 }}
          >
            <OpenInFullIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                {L('ยอด PO แยกตามคู่ค้า/ลูกค้า', 'PO by customer')}
              </Typography>
              {[
                [INCOMPLETE_GREEN, L('มีงานยังไม่เสร็จ 100%', 'Has incomplete project')],
                [LAWSUIT_MAROON, L('มีคดีฟ้องร้อง', 'Has lawsuit')],
              ].map(([color, label]) => (
                <Stack key={label} direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                  <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Chip
                label={L('เรียงตามยอด', 'By amount')}
                size="small"
                onClick={() => setCustomerSort('amount')}
                color={customerSort === 'amount' ? 'primary' : 'default'}
                variant={customerSort === 'amount' ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
              <Chip
                label={L('รับงานล่าสุด', 'By latest job')}
                size="small"
                onClick={() => setCustomerSort('latest')}
                color={customerSort === 'latest' ? 'primary' : 'default'}
                variant={customerSort === 'latest' ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: '0.68rem', color: AXIS_INK, mb: 1 }}>
            {L(
              `รวมทั้งหมด ${customerTotals.count.toLocaleString('th-TH')} โปรเจกต์ · ${fmtBaht(customerTotals.value)} บาท  (ปี ${YEAR_TREND_MIN} เป็นต้นไป)`,
              `Total ${customerTotals.count.toLocaleString('th-TH')} projects · ${fmtBaht(customerTotals.value)} THB  (${YEAR_TREND_MIN} onward)`,
            )}
          </Typography>
          {customerSort === 'latest' && (
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: TEXT_SECONDARY,
                textAlign: 'center',
                mb: 0.5,
              }}
            >
              Latest Project cost
            </Typography>
          )}
          {byCustomer.length ? (
            expandedChart === 'g3' ? (
              // apex mutate object annotation/config ที่ share ข้ามกัน — ถ้าปล่อยการ์ดเล็กเรนเดอร์
              // Chart ซ้อนอยู่พร้อมกับ Dialog เต็มจอ (options object อ้างอิงตัวเดียวกันเป๊ะเพราะ
              // memoized) บางกราฟที่มี annotations.points จะหายไปจากอันที่ mount ทีหลัง (เจอกับ G-11)
              // เลยไม่ mount การ์ดเล็กซ้ำตอนเปิด Dialog ของตัวเองอยู่ กัน 2 instance ชนกัน
              <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }} />
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                <Chart options={customerOptions} series={g3Series} type="bar" height={customerChartHeight} />
              </Box>
            )
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: AXIS_INK, py: 4, textAlign: 'center' }}>
              {L('ไม่มีข้อมูล', 'No data')}
            </Typography>
          )}
        </Card>

        <Card
          sx={{
            position: 'relative',
            minWidth: 0,
            p: 2,
            border: '1px solid #e1e0d9',
            boxShadow: 'none',
            height: TOP_TRIO_CARD_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DevCardBadge num={4} name="Yearly PO & Net Profit" />
          <IconButton
            size="small"
            onClick={() => setExpandedChart('g4')}
            sx={{ position: 'absolute', top: 4, right: 42, zIndex: 5, p: 0.5 }}
          >
            <OpenInFullIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5 }}>
            Yearly PO &amp; Net Profit{' '}
            <Typography component="span" sx={{ fontSize: '0.68rem', fontWeight: 400, color: TEXT_SECONDARY }}>
              {`(${L(`ตั้งแต่ปี ${YEAR_TREND_MIN}`, `${YEAR_TREND_MIN} onward`)})`}
            </Typography>
          </Typography>
          {g4Period === 'year' && (
            <Chip
              label={g4ShowAccumulated ? L('แสดงกราฟแท่ง', 'Show bars') : L('แสดงเส้นสะสม', 'Show accumulated')}
              size="small"
              variant="outlined"
              onClick={() => setG4ShowAccumulated((visible) => !visible)}
              sx={{ position: 'absolute', top: 28, right: 8, height: 22, fontSize: '0.68rem', zIndex: 5 }}
            />
          )}
          <Stack direction="row" spacing={0.75} sx={{ mb: 0.5 }}>
            {[
              ['year', L('รายปี', 'Yearly')],
              ['month', L('รายเดือน', 'Monthly')],
            ].map(([key, label]) => (
              <Chip
                key={key}
                label={label}
                size="small"
                onClick={() => setG4Period(key)}
                color={g4Period === key ? 'primary' : 'default'}
                variant={g4Period === key ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            ))}
          </Stack>
          {/* legend ไตรมาสเอง (แทน legend ของ apex) — อยู่ใน header คงที่ ไม่กินพื้นที่แท่งกราฟ
              จัดกึ่งกลาง เพราะตอนนี้กราฟมีแค่ 2 แท่ง (ปีที่แล้ว/ปีนี้) กึ่งกลางการ์ด = กึ่งกลาง
              ระหว่าง 2 แท่งพอดี — ถ้าปีเยอะขึ้นเรื่อยๆ ในอนาคตจนคู่ YoY ไม่ได้อยู่กึ่งกลางกราฟอีก
              (เช่น กราฟยาวหลายปีแต่ YoY เทียบแค่ปีขวาสุด 2 ปี) ต้องกลับมาคิดใหม่ว่าจะจัดตำแหน่งตาม
              พิกัดจริงของ 2 แท่งนั้นแทนการจัดกึ่งกลางเฉยๆ */}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
            sx={{ mb: 0.5, flexWrap: 'wrap', display: g4Period === 'year' ? 'flex' : 'none' }}
          >
            {QUARTER_COLORS.map((color, qi) => (
              <Stack key={color} direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>{`Q${qi + 1}`}</Typography>
              </Stack>
            ))}
          </Stack>
          {/* สรุป YoY ใช้สีเดียวกับเส้นประ: อดีตมากกว่าปัจจุบันแดง, น้อยกว่าเป็นเขียว */}
          {g4Period === 'year' && byYear.length > 0 && (
            <Typography
              sx={{ fontSize: '0.68rem', color: yoyComparisonColor, mb: 0.5, fontWeight: 600, textAlign: 'center' }}
            >
              {L(
                `YoY ${yoyLastYear} ณ วันนี้: ${fmtBaht(lastYearToDatePo)} บาท (${lastYearToDateCount} โปรเจกต์) (ปีนี้ ${yoyDiff >= 0 ? 'นำหน้า' : 'ช้ากว่า -'}${fmtBaht(Math.abs(yoyDiff))})`,
                `YoY ${yoyLastYear} to date: ${fmtBaht(lastYearToDatePo)} THB (${lastYearToDateCount} projects) (this year ${yoyDiff >= 0 ? 'ahead' : 'behind -'}${fmtBaht(Math.abs(yoyDiff))})`,
              )}
            </Typography>
          )}
          {byYear.length ? (
            // ไม่ mount การ์ดเล็กซ้ำตอน Dialog เต็มจอของตัวเองเปิดอยู่ — ดูคอมเมนต์เหตุผลเต็มที่การ์ด
            // G-3 ด้านบน (apex mutate annotations object ที่ share กันข้าม 2 instance)
            expandedChart === 'g4' ? (
              <Box sx={{ flex: 1, minHeight: 0 }} />
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, ...g4TooltipAvoidSx }}>
                {g4Period === 'month' ? (
                  <MonthTable byMonth={byMonth} L={L} />
                ) : g4ShowAccumulated ? (
                  <Chart
                    options={accumulatedYearOptions}
                    series={accumulatedNetProfitSeries}
                    type="line"
                    height="100%"
                  />
                ) : (
                  <Box className="g4-bar-chart" sx={{ height: '100%' }}>
                    <Chart
                      options={yearOptions}
                      series={[...quarterSeries, profitSeries, idSeries, idProfitSeries]}
                      type="bar"
                      height="100%"
                    />
                  </Box>
                )}
              </Box>
            )
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: AXIS_INK, py: 4, textAlign: 'center' }}>
              {L('ไม่มีข้อมูล', 'No data')}
            </Typography>
          )}
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
            sx={{ mt: 0.25, flexWrap: 'wrap', flexShrink: 0, display: g4Period === 'year' ? 'flex' : 'none' }}
          >
            {g4ShowAccumulated ? (
              <>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 2, bgcolor: NET_PROFIT_POSITIVE }} />
                  <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>
                    {L('Net Profit สะสมเพิ่มขึ้น', 'Accumulated Net Profit increase')}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 12, height: 2, bgcolor: NET_PROFIT_NEGATIVE }} />
                  <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>
                    {L('Net Profit สะสมลดลง', 'Accumulated Net Profit decrease')}
                  </Typography>
                </Stack>
              </>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: G4_PROFIT_GREEN }} />
                  <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>
                    {L('กำไร', 'Profit')}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ID_RED }} />
                  <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>ID PAID</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: `linear-gradient(90deg, ${NET_PROFIT_POSITIVE} 50%, ${NET_PROFIT_NEGATIVE} 50%)`,
                    }}
                  />
                  <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>Net Profit</Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Card>
      </Box>

      {/* G-11/G-10 อยู่แถวเดียวกัน (บรรทัดเดียวกันตามที่ขอ) — เดิมเคยเป็นคนละแถวเต็มความกว้างแยกกัน
          ความสูงต่างกัน (TOP_TRIO_CARD_HEIGHT vs G10_CARD_HEIGHT) ใช้ alignItems:'start' กันไม่ให้
          การ์ดเตี้ยยืดเท่าการ์ดสูงในแถวเดียวกัน (pattern เดียวกับแถว G-3/G-4 ด้านบน) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
          mt: 2,
          alignItems: 'start',
        }}
      >
        <Card
          sx={{
            position: 'relative',
            minWidth: 0,
            p: 2,
            border: '1px solid #e1e0d9',
            boxShadow: 'none',
            height: G10_CARD_HEIGHT, // ขยายสูงเท่า G-10 ที่อยู่แถวเดียวกัน (เดิม TOP_TRIO_CARD_HEIGHT เตี้ยกว่า)
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DevCardBadge num={11} name="Yearly cumulative profit trend" />
          <IconButton
            size="small"
            onClick={() => setExpandedChart('g11')}
            sx={{ position: 'absolute', top: 4, right: 42, zIndex: 5, p: 0.5 }}
          >
            <OpenInFullIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5 }}>
            {L('เส้นเทียบปี — กำไรสะสมรายเดือน (ยังไม่หัก Indirect Cost)', 'YoY cumulative profit (excludes indirect cost)')}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY, mb: 1 }}>
            {L(
              `กำไรสะสมตั้งแต่ ม.ค.–ธ.ค. แยกเส้นตามปี (${YEAR_TREND_MIN} เป็นต้นไป) เทียบ pace ปีต่อปี — ตัวเลขยังไม่ได้หักค่าใช้จ่าย Indirect Cost`,
              `Cumulative profit Jan–Dec, one line per year (${YEAR_TREND_MIN} onward), to compare year-over-year pace — figures do not yet deduct indirect costs`,
            )}
          </Typography>
          {yearlyCumulativeProfit.length ? (
            // ไม่ mount การ์ดเล็กซ้ำตอน Dialog เต็มจอของตัวเองเปิดอยู่ — ดูคอมเมนต์เหตุผลเต็มที่การ์ด
            // G-3 ด้านบน (apex mutate annotations object ที่ share กันข้าม 2 instance — G-11 นี่แหละ
            // ที่เจอบั๊กนี้จริง: ป้าย diff/ยอดรวม/นำหน้า-ช้ากว่า หายไปจาก Dialog ตอนการ์ดเล็กยัง mount ค้างอยู่)
            expandedChart === 'g11' ? (
              <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }} />
            ) : (
              <Box ref={g11ChartWrapRef} onDoubleClick={handleG11DoubleClick} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <Chart options={yearlyProfitLineOptions} series={g11Series} type="line" height="100%" />
              </Box>
            )
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: AXIS_INK, py: 4, textAlign: 'center' }}>
              {L('ไม่มีข้อมูล', 'No data')}
            </Typography>
          )}
        </Card>

        <Card
          sx={{
            position: 'relative',
            minWidth: 0,
            p: 2,
            border: '1px solid #e1e0d9',
            boxShadow: 'none',
            height: G10_CARD_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DevCardBadge num={10} name="Profit/loss by company" />
          <IconButton
            size="small"
            onClick={() => setExpandedChart('g10')}
            sx={{ position: 'absolute', top: 4, right: 42, zIndex: 5, p: 0.5 }}
          >
            <OpenInFullIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
              {L(
                `กำไร/ขาดทุนตามลูกค้า (${companyProfitTop.length} บริษัท)`,
                `Profit/loss by Customers (${companyProfitTop.length} companies)`,
              )}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Chip
                label={L('กำไรสูงสุด', 'Highest profit')}
                size="small"
                onClick={() => setCompanyMetric('profit')}
                color={companyMetric === 'profit' ? 'primary' : 'default'}
                variant={companyMetric === 'profit' ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: '0.66rem' }}
              />
              <Chip
                label={L('%กำไร/BG สูงสุด', 'Highest %profit/BG')}
                size="small"
                onClick={() => setCompanyMetric('bgPct')}
                color={companyMetric === 'bgPct' ? 'primary' : 'default'}
                variant={companyMetric === 'bgPct' ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: '0.66rem' }}
              />
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY, mb: 1 }}>
            {L(
              `กรองเฉพาะบริษัทที่มูลค่าซื้อขายรวม > 100,000 บาท แสดง ${COMPANY_CHART_COUNT} บริษัทที่มีโปรเจกต์ล่าสุดใหม่สุด เรียงเก่า→ใหม่ (ซ้าย→ขวา) — บริษัทที่ขาดทุนการันตีติดกราฟเสมอไม่ว่าจะเก่าแค่ไหน สีเข้ม/ทึบ = ล่าสุดไม่ใช่ปีนี้`,
              `Filtered to companies with total trading value > 100,000 THB, showing the ${COMPANY_CHART_COUNT} most recently active, oldest→newest left to right — loss-making companies are always guaranteed a spot regardless of age; darker/muted color = latest activity not this year`,
            )}
          </Typography>
          {companyProfitTop.length ? (
            // ไม่ mount การ์ดเล็กซ้ำตอน Dialog เต็มจอของตัวเองเปิดอยู่ — ดูคอมเมนต์เหตุผลเต็มที่การ์ด
            // G-3 ด้านบน (apex mutate annotations object ที่ share กันข้าม 2 instance)
            expandedChart === 'g10' ? (
              <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }} />
            ) : (
              <Box ref={g10ChartWrapRef} onDoubleClick={handleG10DoubleClick} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <Chart options={companyProfitOptions} series={g10Series} type="bar" height="100%" />
              </Box>
            )
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: AXIS_INK, py: 4, textAlign: 'center' }}>
              {L('ไม่มีข้อมูล', 'No data')}
            </Typography>
          )}
        </Card>
      </Box>

      {/* G-5/G-6/G-12 — G-12 ย้ายมาต่อท้าย G-6 (เดิมแยกแถวของตัวเองด้านล่างสุด) ตามที่ขอ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
          mt: 2,
        }}
      >
        <RankedCustomerList
          devNum={5}
          devName="Customers with orders this year"
          label={L(
            `คู่ค้าที่มีการสั่งจ้างในปี ${retentionYear} (${activeCustomers.length}/${totalCustomerCount})`,
            `Customers with orders in ${retentionYear} (${activeCustomers.length}/${totalCustomerCount})`,
          )}
          hint={L(
            'มูลค่ารวมมากกว่า 100,000 บาท เรียงจากมากไปน้อย',
            'Total value over 100,000 THB, sorted highest to lowest',
          )}
          entries={activeCustomers}
          color={PROFIT_GREEN}
          emptyLabel={L('ไม่มีข้อมูล', 'No data')}
          onOpenProject={onOpenProject}
        />
        <RankedCustomerList
          devNum={6}
          devName="Past customers, no orders this year"
          label={L(
            `คู่ค้าเก่าที่ไม่มีการสั่งจ้างเลยในปี ${retentionYear} (${churnedCustomers.length}/${totalCustomerCount})`,
            `Past customers with no orders in ${retentionYear} (${churnedCustomers.length}/${totalCustomerCount})`,
          )}
          hint={L(
            `เคยสั่งจ้างมากกว่า 100,000 บาทในปีก่อนหน้า (${priorYearsLabel}) แต่ปี ${retentionYear} เงียบไปเลย`,
            `Previously ordered over 100,000 THB in the prior years (${priorYearsLabel}) but no orders in ${retentionYear}`,
          )}
          entries={churnedCustomers}
          color={TEXT_SECONDARY}
          emptyLabel={L('ไม่มีข้อมูล', 'No data')}
          onOpenProject={onOpenProject}
        />
        <RankedCustomerList
          devNum={12}
          devName="Customers with lawsuits"
          label={L(
            `ลูกค้าที่ถูกฟ้องร้อง (${lawsuitCustomers.length} ราย)`,
            `Customers with lawsuits (${lawsuitCustomers.length})`,
          )}
          hint={L(
            `มูลค่าการฟ้องร้อง = ยอดเงินที่ยังไม่ได้รับ (PO สุทธิ - รับเงินแล้ว) · รวม ${lawsuitTotals.count.toLocaleString('th-TH')} โปรเจกต์ · ${fmtBaht(lawsuitTotals.value)} บาท`,
            `Lawsuit value = unreceived amount (net PO - amount received) · Total ${lawsuitTotals.count.toLocaleString('th-TH')} projects · ${fmtBaht(lawsuitTotals.value)} THB`,
          )}
          entries={lawsuitCustomers}
          color={LAWSUIT_MAROON}
          labelColor={LOSS_RED}
          hintColor={LOSS_RED}
          autoHeight
          emptyLabel={L('ไม่มีคู่ค้าที่ถูกฟ้องร้อง', 'No customers with lawsuits')}
          onOpenProject={onOpenProject}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
          mt: 2,
        }}
      >
        {[
          {
            key: 'profit',
            devNum: 7,
            title: L('กำไรสูงสุด 10 โครงการ', 'Top 10 by profit'),
            entries: topProfit,
            options: topProfitOptions,
          },
          {
            key: 'overBudget',
            devNum: 8,
            title: L('เกิน Budget มากที่สุด 10 โครงการ', 'Top 10 over budget'),
            entries: topOverBudget,
            options: topOverBudgetOptions,
          },
          {
            key: 'loss',
            devNum: 9,
            title: L('ขาดทุนสูงสุด 10 โครงการ', 'Top 10 by loss'),
            entries: topLoss,
            options: topLossOptions,
          },
        ].map(({ key, devNum, title, entries, options }) => (
          <Card
            key={key}
            sx={{
              position: 'relative',
              p: 2,
              border: '1px solid #e1e0d9',
              boxShadow: 'none',
              height: TOP_LIST_CARD_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DevCardBadge num={devNum} name={title} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: key === 'loss' ? 0.25 : 1 }}>
              {title}
            </Typography>
            {key === 'loss' && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: LAWSUIT_MAROON }} />
                <Typography sx={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}>
                  {L('ขาดทุนเพราะมีคดีฟ้องร้อง', 'Loss due to lawsuit')}
                </Typography>
              </Stack>
            )}
            {entries.length ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Chart
                  options={options}
                  series={[{ name: title, data: entries.map(([, v]) => Math.round(v)) }]}
                  type="bar"
                  height="100%"
                />
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: AXIS_INK, py: 4, textAlign: 'center' }}>
                {L('ไม่มีข้อมูล', 'No data')}
              </Typography>
            )}
          </Card>
        ))}
      </Box>


      {/* popover breakdown รายโปรเจกต์ของแท่ง G-3/G-4 — เปิดตอน double-click แท่ง ใช้
          JobBreakdownList ตัวเดียวกับ tooltip G-5/G-6 */}
      <Popover
        open={!!customerPopover}
        onClose={() => setCustomerPopover(null)}
        anchorReference="anchorPosition"
        anchorPosition={customerPopover?.anchorPosition || { top: 0, left: 0 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {customerPopover && (
          <Box sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 0.5 }}>
              {customerPopover.label}
            </Typography>
            <JobBreakdownList
              key={customerPopover.label}
              jobs={customerPopover.jobs}
              total={customerPopover.total}
              // ไม่ปิด popover ตอนเปิด P111 — popup โครงการเปิดซ้อนทับ ปิดทีละอัน (popup ก่อน แล้วค่อย popover)
              onOpenProject={(id) => {
                onOpenProject && onOpenProject(id);
              }}
              showCustomerName={customerPopover.isOthers}
              showBudget={customerPopover.showBudget}
              defaultSort={customerPopover.defaultSort}
            />
          </Box>
        )}
      </Popover>

      {/* popover breakdown ของ G-11 — เปิดตอน double-click จุดในกราฟเทียบปี โชว์ว่ากำไรเดือนนั้นของ
          แต่ละปีที่กำลังเทียบกันมาจากโครงการไหนบ้าง (ดู handleG11DoubleClick ด้านบน) — 1 กลุ่มต่อ 1 ปี
          ใช้ JobBreakdownList ตัวเดียวกับ popover อื่นๆ ซ้อนกันในกล่องเดียว คั่นด้วยหัวข้อสีปีนั้นๆ */}
      <Popover
        open={!!monthBreakdownPopover}
        onClose={() => setMonthBreakdownPopover(null)}
        anchorReference="anchorPosition"
        anchorPosition={monthBreakdownPopover?.anchorPosition || { top: 0, left: 0 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {monthBreakdownPopover && (
          <Box sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 1 }}>
              {L(`กำไรเดือน ${monthBreakdownPopover.monthLabel} มาจาก`, `${monthBreakdownPopover.monthLabel} profit breakdown`)}
            </Typography>
            {monthBreakdownPopover.groups.map((g) => (
              <Box key={g.year} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: g.color, mb: 0.25 }}>
                  {g.year} ({fmtBaht(g.total)} {L('บาท', 'THB')})
                </Typography>
                {g.jobs.length ? (
                  <JobBreakdownList
                    jobs={g.jobs}
                    total={g.total}
                    onOpenProject={(id) => {
                      onOpenProject && onOpenProject(id);
                    }}
                    showCustomerName
                  />
                ) : (
                  <Typography sx={{ fontSize: '0.72rem', color: TEXT_SECONDARY, fontStyle: 'italic' }}>
                    {L('ไม่มีโครงการ', 'No project')}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Popover>

      {/* เปิดกราฟ G-3/G-4/G-10/G-11 เดี่ยวๆ แบบใหญ่เต็มจอ (ปุ่ม OpenInFullIcon มุมบนขวาของแต่ละการ์ด) —
          ใช้ options/series ชุดเดียวกับการ์ดปกติเป๊ะ (g3Series/g10Series/g11Series/quarterSeries ด้านบน)
          แค่ใส่ในกล่องที่สูงกว่าเดิมมาก ไม่ได้สร้างกราฟใหม่หรือคำนวณซ้ำ */}
      <Dialog open={!!expandedChart} onClose={() => setExpandedChart(null)} maxWidth="lg" fullWidth>
        <IconButton
          onClick={() => setExpandedChart(null)}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        {expandedChart === 'g4' && (
          <Stack direction="row" spacing={0.75} sx={{ position: 'absolute', top: 12, left: 16, zIndex: 2 }}>
            {[
              ['year', L('รายปี', 'Yearly')],
              ['month', L('รายเดือน', 'Monthly')],
            ].map(([key, label]) => (
              <Chip
                key={key}
                label={label}
                size="small"
                onClick={() => setG4Period(key)}
                color={g4Period === key ? 'primary' : 'default'}
                variant={g4Period === key ? 'filled' : 'outlined'}
                sx={{ height: 24, fontSize: '0.7rem' }}
              />
            ))}
          </Stack>
        )}
        {expandedChart === 'g4' && g4Period === 'year' && (
          <Chip
            label={g4ShowAccumulated ? L('แสดงกราฟแท่ง', 'Show bars') : L('แสดงเส้นสะสม', 'Show accumulated')}
            size="small"
            variant="outlined"
            onClick={() => setG4ShowAccumulated((visible) => !visible)}
            sx={{ position: 'absolute', top: 12, right: 52, height: 24, fontSize: '0.7rem', zIndex: 2 }}
          />
        )}
        <DialogContent sx={{ height: '82vh', pt: 5 }}>
          {expandedChart === 'g3' && (
            <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
              <Chart options={customerOptions} series={g3Series} type="bar" height={Math.max(customerChartHeight, 600)} />
            </Box>
          )}
          {expandedChart === 'g4' && (
            <Box sx={{ height: '100%', ...g4TooltipAvoidSx }}>
              {g4Period === 'month' ? (
                <MonthTable byMonth={byMonth} L={L} />
              ) : g4ShowAccumulated ? (
                <Chart
                  options={accumulatedYearOptions}
                  series={accumulatedNetProfitSeries}
                  type="line"
                  height="100%"
                />
              ) : (
                <Box className="g4-bar-chart" sx={{ height: '100%' }}>
                  <Chart
                    options={yearOptions}
                    series={[...quarterSeries, profitSeries, idSeries, idProfitSeries]}
                    type="bar"
                    height="100%"
                  />
                </Box>
              )}
            </Box>
          )}
          {expandedChart === 'g10' && (
            // ref/onDoubleClick เดียวกับการ์ดเล็ก (g10ChartWrapRef/handleG10DoubleClick) — ปลอดภัย
            // เหตุผลเดียวกับ g11 ด้านล่าง (การ์ดเล็กไม่ mount ซ้ำตอน Dialog นี้เปิดอยู่)
            <Box ref={g10ChartWrapRef} onDoubleClick={handleG10DoubleClick} sx={{ height: '100%' }}>
              <Chart options={companyProfitOptions} series={g10Series} type="bar" height="100%" />
            </Box>
          )}
          {expandedChart === 'g11' && (
            // ref/onDoubleClick เดียวกับการ์ดเล็ก (g11ChartWrapRef/handleG11DoubleClick) — ใช้ร่วมกัน
            // ได้ปลอดภัยเพราะการ์ดเล็กไม่ mount Chart ของตัวเองตอน Dialog นี้เปิดอยู่แล้ว (ดูเงื่อนไข
            // expandedChart === 'g11' ที่การ์ดเล็กด้านบน) เลยมีแค่กล่องเดียวที่ถือ ref นี้อยู่จริงเสมอ
            <Box ref={g11ChartWrapRef} onDoubleClick={handleG11DoubleClick} sx={{ height: '100%' }}>
              <Chart options={yearlyProfitLineOptions} series={g11Series} type="line" height="100%" />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
